/**
 * Event Hub Tracker (TypeScript) - Platform-Optimized for SEND
 * -----------------------------------------------------------
 * Enterprise-grade client for Event Hub with full platform compliance.
 *
 * Features
 * - ✅ Meta CAPI v21.0 compliance (fbp/fbc + hashed PII)
 * - ✅ GA4 Measurement Protocol (client_id/user_id management)
 * - ✅ Google Ads Enhanced/Click Conversions (gclid/wbraid/gbraid)
 * - ✅ Snapchat CAPI (_scid/sc_click_id + hashed identifiers)
 * - ✅ TikTok Events API (ttclid + context enrichment)
 * - ✅ Identity Stitching (anonymous → known user journey)
 * - ✅ Consent Management (GDPR/CCPA compliance)
 * - ✅ Enhanced Data Collection (device/browser/geo enrichment)
 *
 * Usage for SEND Platform
 *   const tracker = new EventHubTracker({ 
 *     endpoint: '/api/track',
 *     enableEnhancedData: true
 *   });
 *   
 *   // Track page views
 *   tracker.pageView();
 *   
 *   // Track leads (main conversion for SEND)
 *   tracker.lead({ lead_type: 'contact_form', value: 100 });
 *   
 *   // Track contact form submissions
 *   tracker.contact({ form_type: 'main_contact', form_id: 'hero_form' });
 */

import * as Sentry from '@sentry/react'
import { isNativeLocalStorageAvailable, safeLocal } from '../safeStorage'
import { configureGa4, ensureGa4Script, ga4MeasurementId } from './ga4Client'

function readDocumentCookie(): string {
  if (typeof document === 'undefined') return ''
  try {
    return document.cookie || ''
  } catch (e) {
    Sentry.captureMessage('document.cookie read failed', {
      level: 'warning',
      extra: { error: String(e) },
    })
    return ''
  }
}

function writeDocumentCookie(value: string): void {
  if (typeof document === 'undefined') return
  try {
    document.cookie = value
  } catch (e) {
    Sentry.captureMessage('document.cookie write failed', {
      level: 'warning',
      extra: { error: String(e) },
    })
  }
}

// ----------------------------- Types & Enums -----------------------------

export type Currency = string; // e.g., 'SAR', 'USD', 'AED'

export enum EHEventName {
  // Lead Generation Events (main focus for SEND)
  LEAD = 'LEAD',
  CONTACT = 'CONTACT',
  
  // User Journey Events
  PAGE_VIEW = 'PAGE_VIEW',
  SIGN_UP = 'SIGN_UP',
  IDENTIFY = 'IDENTIFY', // Special event for identity stitching
  
  // E-commerce Events (if applicable)
  ADD_CART = 'ADD_CART',
  ADD_BILLING = 'ADD_BILLING',
  START_CHECKOUT = 'START_CHECKOUT',
  PURCHASE = 'PURCHASE',
  VIEW_CONTENT = 'VIEW_CONTENT',
  VIEW_ITEM_LIST = 'VIEW_ITEM_LIST',
  
  // Marketing Events  
  SEARCH = 'SEARCH',
  
  // Engagement Events
  VIEW_ITEM = 'VIEW_ITEM', // GA4 specific
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  
  // ✅ Engagement Tracking Events (لتحسين Activity وتقليل Bounce Rate)
  FORM_INTERACTION = 'FORM_INTERACTION',   // تتبع تفاعل النماذج
  ENGAGEMENT = 'ENGAGEMENT',               // تتبع التفاعل العام (hover، view، إلخ)
}

export type Nullable<T> = T | null | undefined;

export interface EHItem {
  item_id: string;
  item_name?: string;
  quantity?: number;
  price?: number; // per item
  item_brand?: string;
  item_category?: string;
  item_variant?: string;
}

export interface EHUser {
  user_id?: string;          // Internal user ID (for identity stitching)
  anon_id?: string;          // Anonymous ID (eh_anon_id)
  email?: string;            // Will be hashed server-side for platforms
  phone?: string;            // E.164 format e.g. +966501234567
  first_name?: string;       // For enhanced matching
  last_name?: string;        // For enhanced matching
  city?: string;             // Geographic data
  state?: string;            // Geographic data  
  country?: string;          // ISO-2 e.g. 'SA', 'AE', 'US'
  zip?: string;              // Postal code
  gender?: 'm' | 'f';        // For platform optimization
  date_of_birth?: string;    // YYYY-MM-DD format
}

export interface EHConsent {
  // GDPR/CCPA Compliance
  ads_storage?: boolean;           // Google Ads consent
  analytics_storage?: boolean;     // GA4 consent
  ad_personalization?: boolean;    // Meta consent
  npa?: boolean;                   // Non-personalized ads (GA4)
  opt_out?: boolean;               // Global opt-out flag
  
  // Platform-specific consent
  meta_consent?: boolean;          // Meta CAPI consent
  google_consent?: boolean;        // Google platforms consent
  snapchat_consent?: boolean;      // Snapchat consent
  tiktok_consent?: boolean;        // TikTok consent
}

export interface EHClientIds {
  // Meta/Facebook IDs
  fbp?: string;              // Facebook browser pixel (_fbp)
  fbc?: string;              // Facebook click ID (_fbc or derived from fbclid)
  fbclid?: string;           // Facebook click ID from URL
  ctwa_clid?: string;        // Click to WhatsApp ID (from Meta Ads)
  fr?: string;               // Facebook cookie
  
  // Snapchat IDs  
  scid?: string;             // Snapchat cookie (_scid)
  scid_r?: string;           // Snapchat cookie (_scid_r)
  sctr?: string;             // Snapchat conversion tracking (_sctr)
  sc_cbts?: string;          // Snapchat callback timestamp (_ScCbts)
  sc_click_id?: string;      // Snapchat click ID from URL
  sc_at?: string;            // Snapchat authentication token
  x_ab?: string;             // Snapchat X-AB cookie
  
  // Google IDs
  gclid?: string;            // Google click ID
  wbraid?: string;           // Google Ads enhanced conversions
  gbraid?: string;           // Google Ads enhanced conversions  
  ga_client_id?: string;     // GA4 client ID
  _ga?: string;              // Google Analytics cookie
  _gid?: string;             // Google Analytics cookie
  _gat?: string;             // Google Analytics cookie
  _gcl_au?: string;          // Google Ads cookie
  _gcl_aw?: string;          // Google Ads cookie
  _gcl_gs?: string;          // Google Ads cookie
  
  // TikTok IDs
  ttclid?: string;           // TikTok click ID
  ttp?: string;              // TikTok first-party cookie (_ttp)
  tt_enable_cookie?: string; // TikTok cookie
  
  // Twitter/X IDs
  twclid?: string;           // Twitter click ID
  guest_id?: string;         // Twitter guest ID
  guest_id_ads?: string;     // Twitter ads guest ID
  guest_id_marketing?: string; // Twitter marketing guest ID
  personalization_id?: string; // Twitter personalization ID
  
  // Yandex IDs
  yclid?: string;            // Yandex click ID
  _ym_uid?: string;          // Yandex Metrica user ID
  _ym_d?: string;            // Yandex Metrica date
  yandexuid?: string;        // Yandex UID
  yabs_sid?: string;         // Yandex Ads session ID
  ymex?: string;             // Yandex Metrica expire
  yuidss?: string;           // Yandex UID SS
  i?: string;                // Yandex cookie
  bh?: string;               // Yandex cookie
  
  // Microsoft/Bing IDs
  msclkid?: string;          // Microsoft click ID
  
  // Other platform IDs
  li_fat_id?: string;        // LinkedIn click ID
  pin_click_id?: string;     // Pinterest click ID
}

export interface EHSource {
  page_url?: string;
  referrer?: string;
  page_title?: string;
  page_hostname?: string;
  page_pathname?: string;
  search_query?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface EHEnhancedData {
  // Device & Browser Information
  device_type?: string;          // iPhone, Android Phone, Windows PC, etc.
  browser_name?: string;         // Chrome, Safari, Firefox, etc.
  operating_system?: string;     // iOS 17.1, Android 14, Windows 11, etc.
  screen_resolution?: string;    // 1920x1080, 375x812, etc.
  viewport_size?: string;        // 1200x800, etc.
  color_depth?: number;          // 24, 32
  timezone?: string;             // America/New_York, Asia/Riyadh
  language?: string;             // en-US, ar-SA
  
  // Geographic Information (if available/consented)
  user_country?: string;         // SA, US, AE, etc.
  user_city?: string;            // Riyadh, New York, Dubai, etc.
  user_region?: string;          // Riyadh Province, New York, etc.
  
  // Network Information
  connection_type?: string;      // wifi, cellular, ethernet
  effective_type?: string;       // 4g, 3g, 2g, slow-2g
  
  // Performance Metrics
  page_load_time?: number;       // milliseconds
  dom_ready_time?: number;       // milliseconds
  
  // Behavioral Data
  session_duration?: number;     // seconds since session start
  page_views_count?: number;     // number of page views in session
  is_returning_visitor?: boolean; // based on cookie existence
  
  // Technical Details
  javascript_enabled?: boolean;
  cookies_enabled?: boolean;
  local_storage_enabled?: boolean;
  do_not_track?: boolean;        // DNT header value
  
  // Server-side Cookies (collected from document.cookie)
  server_side_cookies?: Record<string, string>; // All cookies from browser
  raw_cookie_header?: string;     // Raw cookie header string
}

export interface EHCommon {
  value?: number;
  currency?: Currency;
  items?: EHItem[];
  // lead-specific (main for SEND)
  lead_type?: string; // 'contact_form', 'newsletter', 'demo_request', etc.
  // purchase-specific
  transaction_id?: string;
  // search-specific
  search_term?: string;
  // content-specific
  content_type?: string; // 'product', 'product_group', 'pricing_page', etc.
  content_category?: string;
  content_name?: string;
  content_ids?: string[];
  // form-specific
  form_type?: string;
  form_id?: string;
  // location-specific (for tracking where event occurred)
  location?: string; // 'home_page', 'pricing_section', etc.
  /** نوع التفاعل مع المحتوى (للتمييز في التقارير، يمر عبر custom_data) */
  interaction_type?: string; // e.g. 'click', 'impression', 'deeplink'
  // page-specific (for page tracking)
  page_name?: string; // 'Home', 'Checkout', 'Success', etc.
  page_title?: string; // Full page title for analytics
  page_category?: string; // Page category for grouping
  /** GA4 view_item_list */
  item_list_id?: string;
  item_list_name?: string;
  /** تجاوز/دمج حقول المصدر (مثلاً أول لمسة تسويقية) */
  source?: EHSource;
}

export interface EHConfig {
  endpoint: string; // your Event Hub intake
  signerEndpoint?: string; // if you require HMAC from server; we POST there instead
  
  // Platform Control
  platformFlags?: {
    meta?: boolean;
    ga4?: boolean;
    google_ads?: boolean;
    snapchat?: boolean;
    tiktok?: boolean;
    mixpanel?: boolean;
    jitsu?: boolean;
    extra?: boolean;
  };
  
  // Data Collection Settings
  enableEnhancedData?: boolean;  // Collect device/browser/geo data
  enableAutoPageTracking?: boolean; // Auto-track page views
  enableAutoClickTracking?: boolean; // Auto-track link clicks
  enableAutoFormTracking?: boolean;  // Auto-track form submissions
  
  // ✅ Engagement Tracking Settings (لتحسين Activity وتقليل Bounce Rate)
  enableScrollTracking?: boolean;      // تتبع عمق التمرير (افتراضي: true)
  enableTimeOnPageTracking?: boolean;  // تتبع الوقت في الصفحة (افتراضي: true)
  enableClickTracking?: boolean;       // تتبع النقرات (افتراضي: true)
  enableFormInteractionTracking?: boolean; // تتبع تفاعل النماذج (افتراضي: true)
  enableEngagementTracking?: boolean;  // تتبع التفاعل العام (افتراضي: true)
  
  // Scroll Tracking Configuration
  scrollThresholds?: number[];         // نقاط التمرير لتتبعها (افتراضي: [25, 50, 75, 90])
  scrollDebounceMs?: number;           // وقت الانتظار قبل تتبع التمرير (افتراضي: 100)
  
  // Time on Page Configuration
  timeOnPageInterval?: number;         // الفترة الزمنية لإرسال TIME_ON_PAGE (بالثواني، افتراضي: 30)
  
  // Default Values
  defaultCountry?: string; // e.g., 'SA'
  defaultCurrency?: Currency; // e.g., 'SAR'
  defaultTimezone?: string; // e.g., 'Asia/Riyadh'
  
  // Integration Helpers
  getGaClientId?: () => string | undefined; // inject GA client id if you run gtag.js on page
  getFacebookPixelId?: () => string | undefined; // inject Meta pixel id
  getSnapchatPixelId?: () => string | undefined; // inject Snapchat pixel id
  
  // Privacy & Consent
  respectDNT?: boolean; // Respect Do Not Track header
  enableConsentMode?: boolean; // Enable consent mode for GDPR/CCPA
  consentDefaults?: EHConsent; // Default consent values
  
  // Debug & Development
  debug?: boolean; // Enable debug logging
  testMode?: boolean; // Enable test mode (adds test_event_code)
  testEventCode?: string; // Test event code for platforms
}

export interface CanonicalEventPayload {
  // Core Event Data
  event_name: EHEventName | string;
  event_time: number; // unix seconds
  event_id: string;
  action_source: 'website' | 'mobile_app' | 'chat' | 'email' | 'phone_call' | 'other';
  
  // User Identity & Client Information
  user: EHUser;
  client_ids: EHClientIds;
  client_data: {
    client_ip_address?: string;    // Will be set by backend from request
    client_user_agent?: string;    // Will be set by backend from request
    client_timestamp?: number;     // Client-side timestamp
    ray_id?: string;               // Cloudflare Ray ID (CF-Ray) or Railway Request ID (X-Railway-Request-Id)
  };
  
  // Event-specific Data
  custom_data?: Record<string, any>;
  enhanced_data?: EHEnhancedData;
  
  // Privacy & Consent
  consent?: EHConsent;
  
  // Context Information
  source?: EHSource;
  
  // Platform-specific Hints (for backend optimization)
  platform_hints?: {
    // Event name mapping hints for backend
    meta_event?: string;           // Meta CAPI event name
    ga4_event?: string;            // GA4 event name
    snapchat_event?: string;       // Snapchat CAPI event name
    tiktok_event?: string;         // TikTok Events API event name
    google_ads_action?: string;    // Google Ads conversion action
    
    // Platform-specific parameters
    meta_params?: Record<string, any>;
    ga4_params?: Record<string, any>;
    snapchat_params?: Record<string, any>;
    tiktok_params?: Record<string, any>;
  };
  
  // Development & Testing
  test_event_code?: string;        // For platform test events
  debug_info?: {
    sdk_version?: string;
    collection_method?: string;    // 'auto' | 'manual'
    validation_errors?: string[];
  };
}

// ----------------------------- Utilities -----------------------------

// ✅ Bot Traffic Filtering (Frontend)
// يطابق نفس المنطق في Backend لضمان التطابق الكامل
function isBotTraffic(): boolean {
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return false;
  }
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  // 1. Check for "bot" keyword (excluding legitimate bots)
  const botPattern = /\bbot\b/i;
  if (botPattern.test(userAgent)) {
    const excludedBots = ['googlebot', 'bingbot', 'slurp', 'baiduspider', 'yandexbot'];
    for (const excluded of excludedBots) {
      if (userAgent.includes(excluded)) {
        return false; // Known legitimate bot
      }
    }
    return true; // Bot detected
  }
  
  // 2. Check for "Mozilla/4.0" pattern (common bot pattern)
  if (userAgent.includes('mozilla/4.0')) {
    return true;
  }
  
  // 3. Check for common bot/scanner patterns
  const botPatterns = [
    'crawler', 'spider', 'scraper', 'curl', 'wget', 'python-requests',
    'go-http-client', 'java/', 'okhttp', 'apache-httpclient',
    'postman', 'insomnia', 'httpie', 'scrapy', 'beautifulsoup',
    'slackbot', 'twitterbot', 'facebookexternalhit', 'linkedinbot',
    'whatsapp', 'telegrambot', 'discordbot', 'skypebot',
    'headless', 'phantom', 'selenium', 'webdriver', 'puppeteer',
    'playwright', 'chromium', 'headlesschrome'
  ];
  
  for (const pattern of botPatterns) {
    if (userAgent.includes(pattern)) {
      return true;
    }
  }
  
  return false;
}

/** UUID v7 (RFC 9562) — يجب أن يبقى متوافقاً مع التحقق في event-hub (common.go / tiktok.go) لـ event_id */
function uuidv7(): string {
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(b);
  } else {
    for (let i = 0; i < 16; i++) {
      b[i] = (Math.random() * 256) | 0;
    }
  }
  const ts = Date.now();
  b[0] = (ts >> 40) & 0xff;
  b[1] = (ts >> 32) & 0xff;
  b[2] = (ts >> 24) & 0xff;
  b[3] = (ts >> 16) & 0xff;
  b[4] = (ts >> 8) & 0xff;
  b[5] = ts & 0xff;
  b[6] = (0x7 << 4) | (b[6] & 0x0f);
  b[8] = (0x2 << 6) | (b[8] & 0x3f);
  const h = (start: number, len: number) =>
    Array.from(b.slice(start, start + len))
      .map((x) => x.toString(16).padStart(2, '0'))
      .join('');
  return `${h(0, 4)}-${h(4, 2)}-${h(6, 2)}-${h(8, 2)}-${h(10, 6)}`;
}

function nowUnix(): number {
  return Math.floor(Date.now() / 1000);
}

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  
  const regex = new RegExp('(?:^|;\\s*)' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)');
  const match = readDocumentCookie().match(regex);
  
  if (match && match[1]) {
    const rawValue = match[1].trim();
    
    // ⚠️ مهم: _sctr cookie يجب أن يبقى مشفراً (URL-encoded) بدون فك الترميز
    // Snapchat يتوقع القيمة المشفرة: "1%7C1768856400000" وليس "1|1768856400000"
    if (name === '_sctr') {
      return rawValue; // ✅ إرجاع القيمة كما هي (مشفرة)
    }
    
    // باقي الكوكيز: فك الترميز كالمعتاد
    try {
      return decodeURIComponent(rawValue);
    } catch (e) {
      return rawValue;
    }
  }
  
  return undefined;
}

function getAllCookies(): Record<string, string> {
  const cookies: Record<string, string> = {};
  
  if (typeof document === 'undefined') {
    return cookies;
  }

  const raw = readDocumentCookie();
  if (!raw) {
    return cookies;
  }

  for (const cookie of raw.split(';')) {
    const trimmed = cookie.trim();
    if (!trimmed) continue;

    const equalIndex = trimmed.indexOf('=');
    if (equalIndex === -1) continue;

    const name = trimmed.substring(0, equalIndex).trim();
    const rawValue = trimmed.substring(equalIndex + 1).trim();

    if (!name) continue;

    if (name === '_sctr') {
      cookies[name] = rawValue;
      continue;
    }

    try {
      cookies[name] = decodeURIComponent(rawValue);
    } catch {
      cookies[name] = rawValue;
    }
  }

  return cookies;
}

function getQuery(name: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const v = params.get(name);
  return v || undefined;
}

function extractGA4ClientId(): string | undefined {
  const gaCookie = getCookie('_ga');
  if (!gaCookie) return undefined;
  
  const parts = gaCookie.split('.');
  if (parts.length >= 4) {
    return `${parts[2]}.${parts[3]}`;
  }
  
  return undefined;
}

function getCookieDomain(): string {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }
  
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }
  
  return '';
}

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  
  const expiryDate = new Date();
  expiryDate.setTime(expiryDate.getTime() + (days * 24 * 60 * 60 * 1000));
  
  const domain = getCookieDomain();
  const cookieOptions = [
    `${name}=${value}`,
    `expires=${expiryDate.toUTCString()}`,
    `path=/`,
    domain ? `domain=${domain}` : '',
    `SameSite=Lax`
  ].filter(Boolean).join('; ');
  
  writeDocumentCookie(cookieOptions);
}

function createFbcFromFbclid(fbclid: string): string {
  if (typeof window === 'undefined') {
    const creationTime = Date.now();
    return `fb.1.${creationTime}.${fbclid}`;
  }

  const hostname = window.location.hostname;
  
  let subdomainIndex = 1;
  
  if (hostname === 'com' || hostname === 'net' || hostname === 'org' || hostname === 'edu') {
    subdomainIndex = 0;
  } else if (hostname.startsWith('www.')) {
    subdomainIndex = 2;
  } else {
    subdomainIndex = 1;
  }

  const creationTime = Date.now();

  const fbc = `fb.${subdomainIndex}.${creationTime}.${fbclid}`;

  setCookie('_fbc', fbc, 90);
  setCookie('fbclid', fbclid, 90);

  return fbc;
}

function createOrGetFbc(): string | undefined {
  const existingFbc = getCookie('_fbc');
  if (existingFbc) {
    const fbclidInUrl = getQuery('fbclid');
    if (fbclidInUrl) {
      const fbcParts = existingFbc.split('.');
      const fbclidInCookie = fbcParts[fbcParts.length - 1];
      
      if (fbclidInUrl !== fbclidInCookie) {
        return createFbcFromFbclid(fbclidInUrl);
      }
    }
    return existingFbc;
  }

  let fbclid = getQuery('fbclid');
  if (fbclid) {
    return createFbcFromFbclid(fbclid);
  }

  fbclid = getCookie('fbclid');
  if (fbclid) {
    return createFbcFromFbclid(fbclid);
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const referrer = document.referrer;
    if (referrer && referrer.includes('facebook.com')) {
      try {
        const referrerUrl = new URL(referrer);
        fbclid = referrerUrl.searchParams.get('fbclid') || undefined;
        if (fbclid) {
          return createFbcFromFbclid(fbclid);
        }
      } catch (e) {
        // Invalid URL
      }
    }
  }

  return undefined;
}

function createOrGetFbp(): string | undefined {
  const existingFbp = getCookie('_fbp');
  if (existingFbp) {
    return existingFbp;
  }

  if (typeof window === 'undefined') return undefined;

  const hostname = window.location.hostname;
  
  let subdomainIndex = 1;
  
  if (hostname === 'com' || hostname === 'net' || hostname === 'org' || hostname === 'edu') {
    subdomainIndex = 0;
  } else if (hostname.startsWith('www.')) {
    subdomainIndex = 2;
  } else {
    subdomainIndex = 1;
  }

  const creationTime = Date.now();

  const randomNumber = Math.floor(Math.random() * 2147483647);

  const fbp = `fb.${subdomainIndex}.${creationTime}.${randomNumber}`;

  setCookie('_fbp', fbp, 90);

  return fbp;
}

// ✅ Click to WhatsApp ID - للتتبع من إعلانات Meta Click to WhatsApp
// يأتي في URL عندما ينقر المستخدم على إعلان Click to WhatsApp
function createOrGetCtwaClid(): string | undefined {
  // أولاً: تحقق من URL الحالي
  const ctwaClid = getQuery('ctwa_clid');
  if (ctwaClid) {
    // حفظ في cookie لاستخدامه لاحقاً (90 يوم)
    setCookie('ctwa_clid', ctwaClid, 90);
    return ctwaClid;
  }
  
  // ثانياً: تحقق من cookie المحفوظ
  const existingCtwaClid = getCookie('ctwa_clid');
  if (existingCtwaClid) {
    return existingCtwaClid;
  }
  
  return undefined;
}

function normalizeEmail(email?: string): string | undefined {
  if (!email) return;
  return email.trim().toLowerCase();
}

function normalizePhone(phone?: string, defaultCountry?: string): string | undefined {
  if (!phone) return;
  let p = phone.replace(/\s+/g, '');
  // Basic normalization for Middle East
  if (!p.startsWith('+')) {
    if (defaultCountry === 'SA') {
      if (p.startsWith('0')) p = p.replace(/^0+/, '');
      p = '+966' + p;
    } else if (defaultCountry === 'AE') {
      if (p.startsWith('0')) p = p.replace(/^0+/, '');
      p = '+971' + p;
    } else if (defaultCountry === 'US') {
      if (p.startsWith('1')) p = p.replace(/^1/, '');
      p = '+1' + p;
    }
  }
  return p;
}

function normalizeCityName(city?: string): string | undefined {
  if (!city) return;
  
  // خريطة تحويل أسماء المدن العربية إلى الإنجليزية
  const cityMap: Record<string, string> = {
    'الرياض': 'Riyadh',
    'جدة': 'Jeddah',
    'الدمام': 'Dammam',
    'مكة المكرمة': 'Mecca',
    'المدينة المنورة': 'Medina',
    'الطائف': 'Taif',
    'الخبر': 'Khobar',
    'بريدة': 'Buraydah',
    'تبوك': 'Tabuk',
    'خميس مشيط': 'Khamis Mushait',
    'حائل': 'Hail',
    'نجران': 'Najran',
    'أبها': 'Abha',
    'الجبيل': 'Jubail',
    'ينبع': 'Yanbu',
    'القصيم': 'Qassim',
    'الشرقية': 'Eastern Province',
    'عسير': 'Asir',
    'جازان': 'Jazan',
    'الباحة': 'Al Baha',
    'الحدود الشمالية': 'Northern Borders',
    'الجوف': 'Al Jawf',
  };
  
  const trimmedCity = city.trim();
  
  // التحقق من وجود المدينة في الخريطة
  if (cityMap[trimmedCity]) {
    return cityMap[trimmedCity];
  }
  
  // إذا لم تكن موجودة، إرجاع الاسم الأصلي (قد يكون بالإنجليزية بالفعل)
  return trimmedCity;
}

// Note: TikTok Pixel SDK automatically hashes PII (email, phone) using SHA-256
// Therefore, we send raw values and let the SDK handle hashing


// Enhanced Data Collection Functions
function getDeviceType(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  
  // Mobile devices
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPod/i.test(ua)) return 'iPod';
  if (/Android/i.test(ua)) {
    if (/tablet|sm-t\d|gt-p\d/i.test(ua)) return 'Android Tablet';
    return 'Android Phone';
  }
  if (/Windows Phone/i.test(ua)) return 'Windows Phone';
  
  // Desktop/Laptop
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac';
  if (/Linux/i.test(ua)) return 'Linux PC';
  if (/CrOS/i.test(ua)) return 'Chromebook';
  
  return 'Unknown';
}

function getBrowserName(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  
  if (/Edg/i.test(ua)) return 'Microsoft Edge';
  if (/Chrome/i.test(ua) && !/Chromium/i.test(ua)) return 'Chrome';
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';
  if (/Firefox/i.test(ua)) return 'Firefox';
  if (/Opera|OPR/i.test(ua)) return 'Opera';
  if (/Trident|MSIE/i.test(ua)) return 'Internet Explorer';
  
  return 'Unknown';
}

function getOperatingSystem(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  
  // Mobile OS
  const iosMatch = ua.match(/OS (\d+)_(\d+)_?(\d+)?/);
  if (iosMatch) {
    return `iOS ${iosMatch[1]}.${iosMatch[2]}${iosMatch[3] ? '.' + iosMatch[3] : ''}`;
  }
  
  const androidMatch = ua.match(/Android (\d+(?:\.\d+)?)/);
  if (androidMatch) {
    return `Android ${androidMatch[1]}`;
  }
  
  // Desktop OS
  if (/Windows NT 10.0/i.test(ua)) return 'Windows 10';
  if (/Windows NT 6.3/i.test(ua)) return 'Windows 8.1';
  if (/Windows NT 6.2/i.test(ua)) return 'Windows 8';
  if (/Windows NT 6.1/i.test(ua)) return 'Windows 7';
  
  const macMatch = ua.match(/Mac OS X (\d+)_(\d+)_?(\d+)?/);
  if (macMatch) {
    return `macOS ${macMatch[1]}.${macMatch[2]}${macMatch[3] ? '.' + macMatch[3] : ''}`;
  }
  
  if (/Linux/i.test(ua)) return 'Linux';
  if (/CrOS/i.test(ua)) return 'Chrome OS';
  
  return 'Unknown';
}

function getScreenInfo(): { resolution: string; viewport: string; colorDepth: number } {
  if (typeof window === 'undefined' || typeof screen === 'undefined') {
    return { resolution: 'Unknown', viewport: 'Unknown', colorDepth: 24 };
  }
  
  return {
    resolution: `${screen.width}x${screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    colorDepth: screen.colorDepth || 24
  };
}

function detectReturningVisitor(): boolean {
  // الكوكيز تُنشأ في الزيارة الأولى فقط — وجودها يعني أن المستخدم عائد
  const hasTrackingCookie = !!(
    getCookie('_scid') ||
    getCookie('_fbp') ||
    getCookie('_ga')
  );
  if (hasTrackingCookie) return true;

  // احتياطي: localStorage للحالات التي لا تتوفر فيها كوكيز التتبع
  try {
    if (safeLocal.getItem('_eh_visited')) {
      return true;
    }
    safeLocal.setItem('_eh_visited', '1');
  } catch {
    // التخزين غير متاح حتى عبر الطبقة الآمنة
  }
  return false;
}

function collectEnhancedData(): EHEnhancedData {
  const screenInfo = getScreenInfo();
  
  return {
    device_type: getDeviceType(),
    browser_name: getBrowserName(),
    operating_system: getOperatingSystem(),
    screen_resolution: screenInfo.resolution,
    viewport_size: screenInfo.viewport,
    color_depth: screenInfo.colorDepth,
    timezone: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return 'Unknown';
      }
    })(),
    language: typeof navigator !== 'undefined' ? (navigator.language || (navigator as any).userLanguage || 'en-US') : 'en-US',
    javascript_enabled: true,
    cookies_enabled: (() => {
      if (typeof document === 'undefined') return false
      try {
        void document.cookie
        return true
      } catch {
        return false
      }
    })(),
    local_storage_enabled: isNativeLocalStorageAvailable(),
    do_not_track: typeof navigator !== 'undefined' && (navigator.doNotTrack === '1' || (window as any).doNotTrack === '1'),
    is_returning_visitor: detectReturningVisitor(),
  };
}

function normalizeEventName(eventName: EHEventName | string): string {
  if (!eventName) return eventName;
  
  const upperEventName = eventName.toUpperCase().trim();
  
  const normalizationMap: Record<string, string> = {
    'ADD_CART': 'ADD_TO_CART',
    'CART_ADD': 'ADD_TO_CART',
    'ADD_TO_CART': 'ADD_TO_CART',
    
    'START_CHECKOUT': 'INITIATE_CHECKOUT',
    'INITIATE_CHECKOUT': 'INITIATE_CHECKOUT',
    'BEGIN_CHECKOUT': 'INITIATE_CHECKOUT',
    
    'ADD_BILLING': 'ADD_PAYMENT_INFO',
    'ADD_PAYMENT_INFO': 'ADD_PAYMENT_INFO',
    
    'SIGN_UP': 'COMPLETE_REGISTRATION',
    'COMPLETE_REGISTRATION': 'COMPLETE_REGISTRATION',
    'REGISTRATION': 'COMPLETE_REGISTRATION',
    
    'VIEW_ITEM': 'VIEW_CONTENT',
    'VIEW_CONTENT': 'VIEW_CONTENT',
    'PRODUCT_VIEW': 'VIEW_CONTENT',
    
    'PURCHASE': 'PURCHASE',
    'ORDER_COMPLETED': 'PURCHASE',
    'COMPLETE_PAYMENT': 'PURCHASE',
    
    'PAGE_VIEW': 'PAGE_VIEW',
    'LEAD': 'LEAD',
    'CONTACT': 'CONTACT',
    'SEARCH': 'SEARCH',
    'LOGIN': 'LOGIN',
    'LOGOUT': 'LOGOUT',
    'IDENTIFY': 'IDENTIFY',
    
    'VIEW_ITEM_LIST': 'VIEW_ITEM_LIST',
  };
  
  if (normalizationMap[upperEventName]) {
    return normalizationMap[upperEventName];
  }
  
  return upperEventName;
}

// ✅ mapMeta: تطابق تماماً مع standardizeEventName في Backend
// هذا يضمن تطابق event_name بين Pixel و CAPI للـ deduplication
const mapMeta: Record<string, string> = {
  'PURCHASE': 'Purchase',
  'ADD_TO_CART': 'AddToCart',
  'VIEW_CONTENT': 'ViewContent',
  'VIEW_ITEM_LIST': 'ViewCategory',
  'INITIATE_CHECKOUT': 'InitiateCheckout',
  'START_CHECKOUT': 'InitiateCheckout', // ✅ إضافة START_CHECKOUT للتطابق مع CAPI
  'ADD_PAYMENT_INFO': 'AddPaymentInfo',
  'ADD_BILLING': 'AddPaymentInfo', // ✅ إضافة ADD_BILLING للتطابق مع CAPI
  'LEAD': 'Lead',
  'COMPLETE_REGISTRATION': 'CompleteRegistration',
  'CONTACT': 'Contact',
  'CUSTOMIZE_PRODUCT': 'CustomizeProduct',
  'DONATE': 'Donate',
  'FIND_LOCATION': 'FindLocation',
  'SCHEDULE': 'Schedule',
  'SEARCH': 'Search',
  'START_TRIAL': 'StartTrial',
  'SUBMIT_APPLICATION': 'SubmitApplication',
  'SUBSCRIBE': 'Subscribe',
  'PAGE_VIEW': 'PageView',
  'LOGIN': 'CompleteRegistration', // Meta لا يوجد لديه Login منفصل
  'LOGOUT': 'PageView',            // Meta لا يوجد لديه Logout منفصل
  'IDENTIFY': 'CompleteRegistration', // Meta لا يوجد لديه Identify منفصل
};

const mapGA4: Record<string, string> = {
  'LEAD': 'generate_lead',
  'CONTACT': 'generate_lead',
  'PAGE_VIEW': 'page_view',
  'COMPLETE_REGISTRATION': 'sign_up',
  'IDENTIFY': 'sign_up',
  'ADD_TO_CART': 'add_to_cart',
  'ADD_PAYMENT_INFO': 'add_payment_info',
  'INITIATE_CHECKOUT': 'begin_checkout',
  'PURCHASE': 'purchase',
  'VIEW_CONTENT': 'view_item',
  'VIEW_ITEM_LIST': 'view_item_list',
  'SEARCH': 'search',
  'LOGIN': 'login',
  'LOGOUT': 'logout',
};

// EventHubTracker Class
export class EventHubTracker {
  private cfg: EHConfig;
  private cachedUser: EHUser = {};
  private consent: EHConsent = {};
  private anonId: string;
  private sessionStartTime: number;
  private pageViewCount: number = 0;
  
  // ✅ Engagement Tracking State (لتحسين Activity)
  private formInteractionListeners: Array<() => void> = []; // Listeners للنماذج
  private cachedRayID: string | null = null;      // ✅ حفظ Ray ID من Response Headers للاستخدام في الطلبات التالية
  private formInputDebounceTimers: Map<string, NodeJS.Timeout> = new Map(); // ✅ Debounce timers للأحداث input
  private lastFocusTracked: Map<string, number> = new Map(); // ✅ آخر وقت تم تتبع focus فيه لكل حقل
  private isFormTrackingSetup: boolean = false; // ✅ للتحقق من عدم إضافة listeners متعددة
  private lastFormInteractionSent: Map<string, number> = new Map(); // ✅ آخر وقت تم إرسال FORM_INTERACTION فيه لكل حقل
  /** سياق جهاز مختم من checkout — يُطبَّق على حدث PURCHASE فقط */
  private purchaseDeviceOverride: {
    user_agent?: string;
    device_type?: string;
    operating_system?: string;
    browser_name?: string;
  } | null = null;

  constructor(cfg: EHConfig) {
    if (!cfg || !cfg.endpoint) throw new Error('EventHubTracker: endpoint is required');
    this.cfg = {
      // Set defaults
      enableEnhancedData: true,
      enableAutoPageTracking: false,
      enableAutoClickTracking: false,
      enableAutoFormTracking: false,
      defaultCountry: 'SA',
      defaultCurrency: 'SAR',
      defaultTimezone: 'Asia/Riyadh',
      respectDNT: false,
      enableConsentMode: false,
      debug: false,
      testMode: false,
      
      // ✅ Engagement Tracking Defaults (لتحسين Activity)
      enableScrollTracking: false, // معطل - تم إزالة Scroll Depth
      enableTimeOnPageTracking: false, // معطل - تم إزالة Time on Page
      enableClickTracking: true,
      enableFormInteractionTracking: true,
      enableEngagementTracking: true,
      scrollThresholds: [25, 50, 75, 90],
      scrollDebounceMs: 100,
      timeOnPageInterval: 30,
      
      ...cfg
    };
    
    this.sessionStartTime = nowUnix();
    this.anonId = this.loadOrCreateAnonId();
    this.initializeConsent();
    
    // ✅ تهيئة تتبع التفاعل إذا كان مفعلاً
    if (typeof window !== 'undefined') {
      this.initializeEngagementTracking();
    }
  }

  private initializeConsent() {
    try {
      const stored = safeLocal.getItem('cookie_consent');
      if (stored === 'accepted') {
        this.consent = {
          ads_storage: true,
          analytics_storage: true,
          ad_personalization: true,
          meta_consent: true,
          google_consent: true,
          snapchat_consent: true,
          tiktok_consent: true,
          opt_out: false,
          npa: false,
        };
        return;
      }
      if (stored === 'rejected') {
        this.consent = {
          ads_storage: false,
          analytics_storage: false,
          ad_personalization: false,
          meta_consent: false,
          google_consent: false,
          snapchat_consent: false,
          tiktok_consent: false,
          opt_out: true,
          npa: true,
        };
        return;
      }
    } catch {
      /* ignore */
    }
    this.consent = {
      ads_storage: false,
      analytics_storage: false,
      ad_personalization: false,
      opt_out: false,
      npa: true,
    };
  }

  setConsent(consent: EHConsent): void {
    this.consent = { ...this.consent, ...consent };
  }

  getConsent(): EHConsent {
    return { ...this.consent };
  }

  // ---------- Public API ----------

  async identify(user: EHUser = {}) {
    const norm: EHUser = {
      ...user,
      anon_id: this.anonId,
      email: normalizeEmail(user.email),
      phone: normalizePhone(user.phone, this.cfg.defaultCountry),
      city: normalizeCityName(user.city), // ✅ تحويل المدينة من العربية إلى الإنجليزية
    };
    
    this.cachedUser = { ...this.cachedUser, ...norm };
    
    try { 
      safeLocal.setItem('eh_user', JSON.stringify(this.cachedUser)); 
    } catch {
      // Failed to save user to storage
    }

    // ✅ Best Practice: Send ttq.identify BEFORE tracking events (EMQ improvement)
    // Reference: TikTok Event Match Quality best practices
    // This improves match rate by 33% and reduces CPA by 30%
    // Note: TikTok Pixel SDK automatically hashes PII, so we send raw values
    if (typeof window !== 'undefined' && typeof (window as any).ttq === 'object') {
      try {
        const ttq = (window as any).ttq;
        const identifyData: any = {};
        
        // ✅ Send email if available (TikTok SDK will hash automatically)
        if (norm.email) {
          identifyData.email = norm.email;
        }
        
        // ✅ Send phone if available (TikTok SDK will hash automatically)
        if (norm.phone) {
          identifyData.phone = norm.phone;
        }
        
        // ✅ Send external_id for Advanced Matching (EMQ improvement)
        // external_id helps TikTok match events to users even without email/phone
        // Use user_id if available, otherwise use anon_id
        const externalId = norm.user_id || this.anonId;
        if (externalId) {
          identifyData.external_id = externalId;
        }
        
        // ✅ Only call ttq.identify if we have at least one identifier
        if (identifyData.email || identifyData.phone || identifyData.external_id) {
          ttq.identify(identifyData);
        }
      } catch (error) {
        // TikTok identify failed silently
      }
    }

    // ✅ Meta Pixel Advanced Matching (EMQ improvement)
    // Reference: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
    // Note: Meta Pixel SDK automatically hashes PII (email, phone, names, etc.)
    if (typeof window !== 'undefined' && typeof (window as any).fbq === 'function') {
      try {
        const fbq = (window as any).fbq;
        const metaUserData: Record<string, string> = {};
        
        // ✅ Email - أهم معرّف للمطابقة
        if (norm.email) {
          metaUserData.em = norm.email;
        }
        
        // ✅ Phone - ثاني أهم معرّف
        if (norm.phone) {
          metaUserData.ph = norm.phone;
        }
        
        // ✅ First Name
        if (norm.first_name) {
          metaUserData.fn = norm.first_name.toLowerCase();
        }
        
        // ✅ Last Name
        if (norm.last_name) {
          metaUserData.ln = norm.last_name.toLowerCase();
        }
        
        // ✅ City
        if (norm.city) {
          metaUserData.ct = norm.city.toLowerCase().replace(/\s+/g, '');
        }
        
        // ✅ State
        if (norm.state) {
          metaUserData.st = norm.state.toLowerCase().replace(/\s+/g, '');
        }
        
        // ✅ Country (ISO-2)
        if (norm.country) {
          metaUserData.country = norm.country.toLowerCase();
        }
        
        // ✅ External ID - للمطابقة بدون PII
        const externalId = norm.user_id || this.anonId;
        if (externalId) {
          metaUserData.external_id = externalId;
        }
        
        // ✅ Only call fbq init if we have at least one identifier
        if (metaUserData.em || metaUserData.ph || metaUserData.external_id) {
          // استخدام init مع userData لتحديث بيانات المستخدم
          // Meta Pixel ID يتم الحصول عليه من environment
          const metaPixelId = typeof import.meta !== 'undefined' 
            ? (import.meta as any).env?.VITE_META_PIXEL_ID 
            : undefined;
          
          if (metaPixelId) {
            fbq('init', metaPixelId, metaUserData);
          }
        }
      } catch (error) {
        // Meta identify failed silently
      }
    }

    this.track(EHEventName.IDENTIFY, {} as any);
  }

  getUser(): EHUser {
    return { ...this.cachedUser };
  }

  getAnonId(): string {
    return this.anonId;
  }

  /** إعادة قراءة eh_anon_id و eh_user من التخزين (مثلاً بعد استعادة سياق من رابط النجاح) */
  hydrateFromLocalStorage(): void {
    this.anonId = this.loadOrCreateAnonId();
    try {
      const fromLs = safeLocal.getItem('eh_user');
      if (fromLs) {
        this.cachedUser = { ...this.cachedUser, ...(JSON.parse(fromLs) as EHUser) };
      }
    } catch {
      /* noop */
    }
  }

  /** استعادة جهاز/متصفح الدفع الحقيقي بعد العودة من بوابة Tap */
  setPurchaseDeviceOverride(ctx: {
    user_agent?: string;
    device_type?: string;
    operating_system?: string;
    browser_name?: string;
  } | null): void {
    this.purchaseDeviceOverride = ctx;
  }

  clearPurchaseDeviceOverride(): void {
    this.purchaseDeviceOverride = null;
  }

  /**
   * ✅ الحصول على Cloudflare Ray ID الحالي
   * 
   * Ray ID يتم استخراجه تلقائياً من Response Headers بعد أول طلب
   * ويتم حفظه في الذاكرة للاستخدام في الطلبات التالية
   * 
   * @returns {string | null} Ray ID الحالي أو null إذا لم يكن متوفراً بعد
   * 
   * @example
   * const rayId = eventHub.getRayID();
   * if (rayId) {
   *   console.log('Ray ID:', rayId);
   *   // يمكن استخدامه لإرساله إلى Mixpanel مباشرة إذا لزم الأمر
   * }
   */
  getRayID(): string | null {
    return this.cachedRayID;
  }

  getAllCookies(): Record<string, string> {
    return getAllCookies();
  }

  getClientIds(): EHClientIds {
    // Meta/Facebook
    const fbc = createOrGetFbc();
    const fbp = createOrGetFbp();
    const fbclid = getQuery('fbclid') || getCookie('fbclid');
    const ctwa_clid = createOrGetCtwaClid(); // Click to WhatsApp ID
    const fr = getCookie('fr');
    
    // Snapchat
    const scid = getCookie('_scid');
    const scid_r = getCookie('_scid_r');
    const sctr = getCookie('_sctr');
    const scCbts = getCookie('_ScCbts');
    const scClickId = getQuery('ScCid');
    const sc_at = getCookie('sc_at');
    const x_ab = getCookie('X-AB');
    
    // Google
    const gclid = getQuery('gclid') || getCookie('gclid');
    const _ga = getCookie('_ga');
    const _gid = getCookie('_gid');
    const _gat = getCookie('_gat');
    const _gcl_au = getCookie('_gcl_au');
    const _gcl_aw = getCookie('_gcl_aw');
    const _gcl_gs = getCookie('_gcl_gs');
    const ga_client_id = this.cfg.getGaClientId ? this.cfg.getGaClientId() : extractGA4ClientId();
    
    // TikTok
    let ttclid = getQuery('ttclid') || getCookie('ttclid');
    
    if (getQuery('ttclid') && !getCookie('ttclid')) {
      setCookie('ttclid', getQuery('ttclid')!, 7); // ttclid صالح 7 أيام حسب توثيق تيك توك
      ttclid = getQuery('ttclid');
    }
    
    const ttp = getCookie('_ttp');
    const tt_enable_cookie = getCookie('_tt_enable_cookie');
    
    // Twitter/X
    const twclid = getQuery('twclid') || getCookie('twclid');
    const guest_id = getCookie('guest_id');
    const guest_id_ads = getCookie('guest_id_ads');
    const guest_id_marketing = getCookie('guest_id_marketing');
    const personalization_id = getCookie('personalization_id');
    
    // Yandex
    const yclid = getQuery('yclid') || getCookie('yclid');
    const _ym_uid = getCookie('_ym_uid');
    const _ym_d = getCookie('_ym_d');
    const yandexuid = getCookie('yandexuid');
    const yabs_sid = getCookie('yabs-sid');
    const ymex = getCookie('ymex');
    const yuidss = getCookie('yuidss');
    const i = getCookie('i');
    const bh = getCookie('bh');
    
    // Microsoft
    const msclkid = getQuery('msclkid') || getCookie('msclkid');
    
    // LinkedIn
    const li_fat_id = getQuery('li_fat_id') || getCookie('li_fat_id');
    
    // Pinterest
    const pin_click_id = getQuery('pin_click_id') || getCookie('pin_click_id');
    
    return {
      // Meta/Facebook
      fbp: fbp || undefined,
      fbc: fbc || undefined,
      fbclid: fbclid || undefined,
      ctwa_clid: ctwa_clid || undefined, // Click to WhatsApp ID
      fr: fr || undefined,
      
      // Snapchat
      scid: scid || undefined,
      scid_r: scid_r || undefined,
      sctr: sctr || undefined,
      sc_cbts: scCbts || undefined,
      sc_click_id: scClickId || undefined,
      sc_at: sc_at || undefined,
      x_ab: x_ab || undefined,
      
      // Google
      gclid: gclid || undefined,
      wbraid: getQuery('wbraid') || getCookie('wbraid') || undefined,
      gbraid: getQuery('gbraid') || getCookie('gbraid') || undefined,
      ga_client_id: ga_client_id,
      _ga: _ga || undefined,
      _gid: _gid || undefined,
      _gat: _gat || undefined,
      _gcl_au: _gcl_au || undefined,
      _gcl_aw: _gcl_aw || undefined,
      _gcl_gs: _gcl_gs || undefined,
      
      // TikTok
      ttclid: ttclid || undefined,
      ttp: ttp || undefined,
      tt_enable_cookie: tt_enable_cookie || undefined,
      
      // Twitter/X
      twclid: twclid || undefined,
      guest_id: guest_id || undefined,
      guest_id_ads: guest_id_ads || undefined,
      guest_id_marketing: guest_id_marketing || undefined,
      personalization_id: personalization_id || undefined,
      
      // Yandex
      yclid: yclid || undefined,
      _ym_uid: _ym_uid || undefined,
      _ym_d: _ym_d || undefined,
      yandexuid: yandexuid || undefined,
      yabs_sid: yabs_sid || undefined,
      ymex: ymex || undefined,
      yuidss: yuidss || undefined,
      i: i || undefined,
      bh: bh || undefined,
      
      // Microsoft
      msclkid: msclkid || undefined,
      
      // LinkedIn
      li_fat_id: li_fat_id || undefined,
      
      // Pinterest
      pin_click_id: pin_click_id || undefined,
    };
  }

  debugTrackingData(): void {
    // Debug data collection - silent mode
  }

  reset() {
    this.cachedUser = { anon_id: this.anonId };
    try {
      safeLocal.removeItem('eh_user');
    } catch {
      // Failed to remove user from storage
    }
  }

  // ========== Event Methods ==========

  pageView(extra?: Partial<EHCommon>) {
    this.pageViewCount++;
    
    // ✅ إعادة تهيئة تتبع التفاعل عند تغيير الصفحة
    this.resetPageTracking();
    
    // ✅ بدء تتبع التفاعل للصفحة الجديدة
    if (typeof window !== 'undefined') {
      this.startPageTracking();
    }
    
    return this.track(EHEventName.PAGE_VIEW, extra || {});
  }

  // ========== ✅ Engagement Tracking Methods (لتحسين Activity) ==========

  /**
   * تهيئة تتبع التفاعل عند إنشاء الـ tracker
   */
  private initializeEngagementTracking(): void {
    if (typeof window === 'undefined') return;
    
    // تهيئة تتبع الصفحة الأولى
    this.startPageTracking();
  }

  /**
   * بدء تتبع التفاعل للصفحة الحالية
   */
  private startPageTracking(): void {
    if (typeof window === 'undefined') return;
    
    // ✅ تتبع التمرير - محذوف
    // ✅ تتبع الوقت في الصفحة - محذوف
    // ✅ تتبع النقرات - محذوف
    
    // ✅ تتبع تفاعل النماذج
    if (this.cfg.enableFormInteractionTracking !== false) {
      this.setupFormInteractionTracking();
    }
  }

  /**
   * إعادة تعيين تتبع الصفحة (عند تغيير الصفحة)
   */
  private resetPageTracking(): void {
    // إيقاف الـ timers والـ listeners
    this.cleanupEngagementTracking();
  }

  /**
   * تنظيف الـ listeners والـ timers
   */
  private cleanupEngagementTracking(): void {
    // إزالة form interaction listeners
    this.formInteractionListeners.forEach(cleanup => cleanup());
    this.formInteractionListeners = [];
    this.isFormTrackingSetup = false; // ✅ إعادة تعيين flag
  }


  /**
   * إعداد تتبع تفاعل النماذج
   * ✅ يتم تتبع submit فقط (عند النقر على زر الإرسال)
   */
  private setupFormInteractionTracking(): void {
    if (typeof document === 'undefined') return;
    
    // ✅ منع إضافة listeners متعددة
    if (this.isFormTrackingSetup) {
      return;
    }
    
    // ✅ تنظيف أي listeners موجودة مسبقاً لتجنب التكرار
    this.cleanupEngagementTracking();
    
    // ✅ تتبع submit فقط (عند النقر على زر الإرسال)
    const handleFormSubmit = (e: SubmitEvent) => {
      const form = e.target as HTMLFormElement;
      if (!form || form.tagName !== 'FORM') return;
      
      // ✅ تتبع submit للنموذج
      this.trackFormInteraction({
        interaction_type: 'submit',
        form_id: form.id || form.getAttribute('name') || undefined,
      });
    };
    
    // ✅ إضافة listener لـ submit على مستوى document
    document.addEventListener('submit', handleFormSubmit, true);
    
    // ✅ تعيين flag بعد إضافة listeners
    this.isFormTrackingSetup = true;
    
    // حفظ cleanup functions
    this.formInteractionListeners.push(() => {
      document.removeEventListener('submit', handleFormSubmit, true);
      
      // ✅ تنظيف جميع timers عند cleanup
      this.formInputDebounceTimers.forEach(timer => clearTimeout(timer));
      this.formInputDebounceTimers.clear();
      this.lastFocusTracked.clear();
      this.lastFormInteractionSent.clear(); // ✅ تنظيف سجل الأحداث المرسلة
    });
  }

  /**
   * تتبع تفاعل النماذج
   */
  trackFormInteraction(data: {
    interaction_type: 'focus' | 'input' | 'submit' | 'error';
    field_name?: string;
    field_type?: string;
    form_id?: string;
    error_message?: string;
  }): void {
    // ✅ حماية من التكرار: منع إرسال نفس الحدث خلال 3 ثواني
    const MIN_INTERVAL_MS = 3000; // 3 ثواني بين الأحداث
    const interactionKey = `${data.interaction_type}_${data.field_name || 'unknown'}_${data.form_id || 'unknown'}`;
    const now = Date.now();
    const lastSent = this.lastFormInteractionSent.get(interactionKey) || 0;
    
    if (now - lastSent < MIN_INTERVAL_MS) {
      return; // ✅ إيقاف التنفيذ لتجنب التكرار
    }
    
    // ✅ تحديث آخر وقت إرسال
    this.lastFormInteractionSent.set(interactionKey, now);
    
    this.track(EHEventName.FORM_INTERACTION, {
      ...data,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
    } as any);
    
  }

  /**
   * تتبع التفاعل العام (للاستخدام اليدوي)
   */
  trackEngagement(data: {
    engagement_type: 'hover' | 'view' | 'interaction' | 'video_play' | 'image_view';
    element_type?: string;
    element_id?: string;
    element_name?: string;
    duration?: number; // بالثواني
  }): void {
    this.track(EHEventName.ENGAGEMENT, {
      ...data,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
    } as any);
    
  }

  lead(data?: {
    lead_type?: string;
    value?: number;
    currency?: Currency;
  } & Partial<EHCommon>) {
    return this.track(EHEventName.LEAD, data || {});
  }

  contact(data?: {
    form_type?: string;
    form_id?: string;
  } & Partial<EHCommon>) {
    return this.track(EHEventName.CONTACT, data || {});
  }

  signUp(data?: {
    method?: string;
  } & Partial<EHCommon>) {
    return this.track(EHEventName.SIGN_UP, data || {});
  }

  login(data?: {
    method?: string;
  } & Partial<EHCommon>) {
    return this.track(EHEventName.LOGIN, data || {});
  }

  logout() {
    // ✅ تنظيف تتبع التفاعل قبل logout
    this.cleanupEngagementTracking();
    
    const result = this.track(EHEventName.LOGOUT, {});
    this.reset();
    return result;
  }

  search(data: {
    search_term: string;
    results_count?: number;
  } & Partial<EHCommon>) {
    return this.track(EHEventName.SEARCH, data);
  }

  viewContent(data: { 
    item_id: string; 
    item_name?: string; 
    price?: number; 
    currency?: Currency;
  } & Partial<EHCommon>) {
    const { item_id, item_name, price, currency, content_type, content_category, ...rest } = data;
    
    let validContentType: string | undefined = undefined;
    if (content_type) {
      if (content_type === "product" || content_type === "product_group") {
        validContentType = content_type;
      } else {
        validContentType = "product";
      }
    } else {
      validContentType = "product";
    }
    
    // ✅ إصلاح: إضافة item_category إلى items array من content_category
    const item: EHItem = {
      item_id,
      item_name,
      price,
      quantity: 1
    };
    
    // إضافة item_category إذا كان content_category موجوداً
    if (content_category) {
      item.item_category = content_category;
    }
    
    return this.track(EHEventName.VIEW_CONTENT, { 
      ...rest, 
      items: [item], 
      value: price, // ✅ إصلاح: إرسال price كـ value لـ TikTok
      currency: currency || this.cfg.defaultCurrency,
      content_ids: [item_id],
      content_type: validContentType,
      content_name: item_name,
      content_category: content_category // ✅ سيتم تحويله إلى category في buildCustomData
    });
  }

  viewItemList(data: {
    items: EHItem[];
    item_list_id?: string;
    item_list_name?: string;
    currency?: Currency;
  } & Partial<EHCommon>) {
    const { items, item_list_id, item_list_name, currency, ...rest } = data;
    if (!items || items.length === 0) {
      return Promise.resolve(false);
    }
    let total = 0;
    for (const it of items) {
      const q = it.quantity && it.quantity > 0 ? it.quantity : 1;
      total += (it.price ?? 0) * q;
    }
    const contentIds = items.map((i) => i.item_id);
    return this.track(EHEventName.VIEW_ITEM_LIST, {
      ...rest,
      items,
      item_list_id,
      item_list_name,
      currency: currency || this.cfg.defaultCurrency,
      value: total > 0 ? total : undefined,
      content_type: 'product',
      content_name: item_list_name,
      content_ids: contentIds,
    });
  }

  addCart(data: {
    value?: number;
    currency?: Currency;
  } & Partial<EHCommon>) {
    return this.track(EHEventName.ADD_CART, {
      ...data,
      currency: data.currency || this.cfg.defaultCurrency
    });
  }

  startCheckout(data?: {
    value?: number;
    currency?: Currency;
  } & Partial<EHCommon>) {
    return this.track(EHEventName.START_CHECKOUT, {
      ...data,
      currency: data?.currency || this.cfg.defaultCurrency
    });
  }

  purchase(data: {
    transaction_id: string;
    value: number;
    currency?: Currency;
  } & Partial<EHCommon>) {
    return this.track(EHEventName.PURCHASE, {
      ...data,
      currency: data.currency || this.cfg.defaultCurrency
    });
  }

  private validateEventData(event: EHEventName | string, common: Partial<EHCommon>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!event || typeof event !== 'string' || event.trim() === '') {
      errors.push('event_name is required and must be a non-empty string');
    }

    if (common.value !== undefined && common.value !== null) {
      if (typeof common.value !== 'number' || common.value < 0 || !isFinite(common.value)) {
        errors.push('value must be a valid positive number');
      }
    }

    if (common.currency && typeof common.currency === 'string') {
      if (common.currency.length !== 3 || !/^[A-Z]{3}$/.test(common.currency)) {
        errors.push('currency must be a valid 3-letter ISO currency code (e.g., SAR, USD)');
      }
    }

    if (common.lead_type && typeof common.lead_type !== 'string') {
      errors.push('lead_type must be a string if provided');
    }

    if (common.form_type && typeof common.form_type !== 'string') {
      errors.push('form_type must be a string if provided');
    }

    if (common.transaction_id && typeof common.transaction_id !== 'string') {
      errors.push('transaction_id must be a string if provided');
    }

    if (common.search_term && typeof common.search_term !== 'string') {
      errors.push('search_term must be a string if provided');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * ينتظر كوكيز البيكسل قبل قراءة client_ids لـ PAGE_VIEW — مطابق لمشروع SANA.
   */
  private async waitForPageViewPixelCookies(): Promise<void> {
    if (typeof window === 'undefined') return;

    const snapEnv = String(import.meta.env.VITE_SNAPCHAT_PIXEL_ID ?? '').trim();
    const wantSnap = !!(snapEnv || typeof (window as unknown as { snaptr?: unknown }).snaptr === 'function');
    if (wantSnap) {
      const deadline = Date.now() + 12_000;
      while (Date.now() < deadline && !getCookie('_scid')) {
        await new Promise(r => setTimeout(r, 100));
      }
      if (getCookie('_scid')) {
        const t2 = Date.now() + 2_000;
        while (Date.now() < t2 && !getCookie('_sctr') && !getCookie('_ScCbts')) {
          await new Promise(r => setTimeout(r, 80));
        }
      }
    }

    if (document.getElementById('yandex-metrica-script')) {
      const deadline = Date.now() + 12_000;
      while (Date.now() < deadline && !getCookie('_ym_uid')) {
        await new Promise(r => setTimeout(r, 100));
      }
    }

    const ttEnv = String(import.meta.env.VITE_TIKTOK_PIXEL_ID ?? '').trim();
    if (ttEnv) {
      const deadline = Date.now() + 14_000;
      while (
        Date.now() < deadline &&
        !getCookie('_ttp') &&
        !getCookie('ttcsid') &&
        !getCookie('_tt_enable_cookie')
      ) {
        await new Promise(r => setTimeout(r, 120));
      }
    }
  }

  // Core tracking method
  async track(event: EHEventName | string, common: Partial<EHCommon> = {}): Promise<boolean> {
    // ✅ Bot Traffic Filtering (Frontend) - للتطابق الكامل مع Backend
    if (isBotTraffic()) {
      return false;
    }
    
    if (this.consent.opt_out) {
      return false;
    }

    const validation = this.validateEventData(event, common);
    if (!validation.isValid) {
      if (!this.cfg.debug) {
        const criticalErrors = validation.errors.filter(e => 
          e.includes('event_name') || e.includes('value') && e.includes('must be a valid')
        );
        if (criticalErrors.length > 0) {
          return false;
        }
      }
    }

    const event_time = nowUnix();

    const normalizedEventName = normalizeEventName(event);
    if (!normalizedEventName || normalizedEventName.trim() === '') {
      return false;
    }

    // ✅ PURCHASE: event_id ثابت لكل transaction_id (منع تكرار الواجهة + السيرفر + إعادة المحاولة)
    let event_id: string;
    if (normalizedEventName === 'PURCHASE' && common.transaction_id?.trim()) {
      const { purchaseEventIdFromTransaction } = await import('./purchaseEventId');
      event_id = (await purchaseEventIdFromTransaction(common.transaction_id)) || uuidv7();
    } else {
      event_id = uuidv7();
    }
    if (!event_id || event_id.trim() === '') {
      return false;
    }

    if (normalizedEventName === 'PAGE_VIEW') {
      await this.waitForPageViewPixelCookies();
    }

    const clientIds = this.getClientIds();

    // ✅ إصلاح: استخدام page_title من common أولاً، ثم document.title فقط إذا لم يكن موجوداً
    // هذا يضمن استخدام العنوان الصحيح حتى لو كان document.title لم يتم تحديثه بعد
    const pageTitle = common.page_title || (typeof document !== 'undefined' ? document.title : undefined);
    
    // ✅ Traffic Attribution: Store initial referrer for first-touch attribution
    // https://docs.mixpanel.com/docs/tracking-best-practices/traffic-attribution
    const currentReferrer = typeof document !== 'undefined' ? document.referrer || undefined : undefined;
    
    // Store initial referrer if this is the first visit (Mixpanel Best Practice)
    if (typeof window !== 'undefined' && currentReferrer) {
      const initialReferrer = getCookie('mp_initial_referrer');
      if (!initialReferrer) {
        // First visit - store initial referrer
        setCookie('mp_initial_referrer', currentReferrer, 365); // 1 year
        
        // Also store initial referring domain
        try {
          const referrerUrl = new URL(currentReferrer);
          const initialReferringDomain = referrerUrl.hostname;
          setCookie('mp_initial_referring_domain', initialReferringDomain, 365);
        } catch (e) {
          // Invalid URL, skip
        }
      }
    }
    
    let source: EHSource = {
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      referrer: currentReferrer,
      page_title: pageTitle,
      page_hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
      page_pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
      search_query: typeof window !== 'undefined' ? window.location.search || undefined : undefined,
      utm_source: getQuery('utm_source') || undefined,
      utm_medium: getQuery('utm_medium') || undefined,
      utm_campaign: getQuery('utm_campaign') || undefined,
      utm_term: getQuery('utm_term') || undefined,
      utm_content: getQuery('utm_content') || undefined,
    };

    try {
      const { mergeFirstTouchIntoSource } = await import('../attributionFirstTouch');
      source = mergeFirstTouchIntoSource(source);
    } catch {
      /* noop */
    }

    if (common.source && typeof common.source === 'object') {
      source = { ...source, ...common.source };
    }

    const user = this.getCurrentUser();
    let enhanced_data = this.cfg.enableEnhancedData ? this.getEnhancedData() : undefined;

    if (normalizedEventName === 'PURCHASE' && this.purchaseDeviceOverride) {
      const o = this.purchaseDeviceOverride;
      enhanced_data = {
        ...(enhanced_data ?? {}),
        ...(o.device_type ? { device_type: o.device_type } : {}),
        ...(o.operating_system ? { operating_system: o.operating_system } : {}),
        ...(o.browser_name ? { browser_name: o.browser_name } : {}),
      };
    }

    const platform_hints = {
      meta_event: mapMeta[normalizedEventName] || undefined,
      ga4_event: mapGA4[normalizedEventName] || undefined,
    };

    const client_data: CanonicalEventPayload['client_data'] = {
      client_timestamp: event_time,
    };
    if (normalizedEventName === 'PURCHASE' && this.purchaseDeviceOverride?.user_agent) {
      client_data.client_user_agent = this.purchaseDeviceOverride.user_agent;
    }

    // ✅ الحل 3: تحسين التطابق - استخدام normalizedEventName في payload
    const payload: CanonicalEventPayload = {
      event_name: normalizedEventName, // ✅ استخدام الاسم الموحد بدلاً من event الأصلي
      event_time,
      event_id,
      action_source: 'website',
      user,
      client_ids: clientIds,
      client_data,
      custom_data: this.buildCustomData(normalizedEventName, common), // ✅ استخدام الاسم الموحد
      enhanced_data,
      source,
      platform_hints,
      consent: this.consent,
      test_event_code: this.cfg.testMode ? this.cfg.testEventCode : undefined,
      debug_info: this.cfg.debug ? {
        sdk_version: '2.0.0-send',
        collection_method: 'manual',
        // ✅ ملاحظة: original_event_name و normalized_event_name محفوظة في logs فقط
        // لا يمكن إضافتها لـ debug_info لأنها ليست جزءاً من الـ type
      } : undefined,
    };

    // سلوك مطابق لمشروع SANA: Pixels ثم مهلة قصيرة؛ ثم لا يُستدعى sendWithRetry(CAPI) إلا بعد ذلك
    payload.event_name = normalizedEventName;

    const flags = this.cfg.platformFlags;

    try {
      if (flags?.tiktok) {
        await this.sendToTikTokPixel(normalizedEventName, event_id, payload.custom_data, payload.user);
      }
    } catch {
      // TikTok Pixel send failed
    }

    try {
      await Promise.all([
        flags?.meta ? this.sendToMetaPixel(normalizedEventName, event_id, payload.custom_data) : Promise.resolve(false),
        flags?.snapchat
          ? this.sendToSnapchatPixel(normalizedEventName, event_id, payload.custom_data, payload.enhanced_data, payload.user, payload.client_ids)
          : Promise.resolve(false),
      ]);
    } catch {
      /* noop */
    }

    if (flags?.ga4 !== false) {
      void ensureGa4Script(ga4MeasurementId()).then(() => {
        this.sendToGA4(normalizedEventName, event_id, payload.custom_data);
      });
    }
    void this.sendToYandexPixel(normalizedEventName, event_id, payload.custom_data);

    await new Promise<void>(resolve => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }
      window.setTimeout(resolve, 450);
    });

    if (normalizedEventName === 'PAGE_VIEW' && typeof window !== 'undefined') {
      if (getCookie('_scid') && !getCookie('_sctr') && !getCookie('_ScCbts')) {
        const d = Date.now() + 3_000;
        while (Date.now() < d && !getCookie('_sctr') && !getCookie('_ScCbts')) {
          await new Promise(r => setTimeout(r, 100));
        }
      }
      if (document.getElementById('yandex-metrica-script') && !getCookie('_ym_uid')) {
        const d = Date.now() + 5_000;
        while (Date.now() < d && !getCookie('_ym_uid')) {
          await new Promise(r => setTimeout(r, 120));
        }
      }
    }

    const capiPromise = this.sendWithRetry(payload, 2, 500, 2, 2000, 15000);

    let capiSuccess = false;
    try {
      capiSuccess = await Promise.race([
        capiPromise,
        new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 15000)),
      ]);
    } catch {
      capiSuccess = false;
    }

    if (!capiSuccess) {
      void this.sendWithRetry(payload, 3, 1000, 2, 30000, 60000).catch(() => {});
    }


    // ✅ إرجاع true إذا نجح إرسال CAPI (Pixel SDK يُرسل دائماً حتى لو فشل)
    return capiSuccess;
  }

  // ---------- Internals ----------

  private buildCustomData(_event: EHEventName | string, common: Partial<EHCommon>): Record<string, any> {
    const cd: Record<string, any> = {};
    
    // ✅ Core fields
    if (common.value != null) cd.value = common.value;
    if (common.currency) cd.currency = common.currency;
    
    // ✅ Purchase-specific - مهم جداً!
    if (common.transaction_id) cd.transaction_id = common.transaction_id;
    
    // ✅ Lead-specific
    if (common.lead_type) cd.lead_type = common.lead_type;
    
    // ✅ Form-specific
    if (common.form_type) cd.form_type = common.form_type;
    if (common.form_id) cd.form_id = common.form_id;
    
    // ✅ Search-specific
    if (common.search_term) cd.search_term = common.search_term;
    
    // ✅ Items array - إرسال جميع الحقول للتطابق الكامل مع Backend
    if (common.items && common.items.length > 0) {
      cd.items = common.items.map(item => {
        const itemData: Record<string, any> = {
          id: item.item_id,
          name: item.item_name,
          category: item.item_category,
          quantity: item.quantity || 1,
          price: item.price
        };
        // إضافة الحقول الاختيارية إذا كانت موجودة
        if (item.item_brand) itemData.brand = item.item_brand;
        if (item.item_variant) itemData.variant = item.item_variant;
        return itemData;
      });
    }
    
    // ✅ Content-specific
    if (common.content_type) cd.content_type = common.content_type;
    if (common.content_category) cd.category = common.content_category;
    if (common.content_name) cd.content_name = common.content_name;
    if (common.content_ids && common.content_ids.length > 0) {
      cd.content_ids = common.content_ids;
    }
    
    // ✅ Location-specific
    if (common.location) cd.location = common.location;
    if (common.interaction_type) cd.interaction_type = common.interaction_type;
    
    // ✅ Page-specific
    if (common.page_name) cd.page_name = common.page_name;
    if (common.page_title) cd.page_title = common.page_title;
    if (common.page_category) cd.page_category = common.page_category;
    
    if (common.item_list_id) cd.item_list_id = common.item_list_id;
    if (common.item_list_name) cd.item_list_name = common.item_list_name;
    
    return cd;
  }

  private getCurrentUser(): EHUser {
    try {
      const fromLs = safeLocal.getItem('eh_user');
      if (fromLs) {
        this.cachedUser = { ...this.cachedUser, ...(JSON.parse(fromLs) as EHUser) };
      }
    } catch {
      // Failed to load user from storage
    }
    
    return { 
      ...this.cachedUser,
      anon_id: this.anonId 
    };
  }

  private getEnhancedData(): EHEnhancedData {
    const enhancedData = collectEnhancedData();
    enhancedData.session_duration = nowUnix() - this.sessionStartTime;
    enhancedData.page_views_count = this.pageViewCount;
    return enhancedData;
  }

  private loadOrCreateAnonId(): string {
    try {
      const k = 'eh_anon_id';
      const existing = safeLocal.getItem(k);
      if (existing) {
        return existing;
      }
      const id = uuidv7();
      safeLocal.setItem(k, id);
      return id;
    } catch {
      // Failed to access storage for anon_id
    }
    return uuidv7();
  }

  // ✅ Retry Mechanism: إعادة المحاولة عند فشل الإرسال (Best Practices)
  // 
  // أفضل الممارسات (Best Practices):
  // 1. Exponential Backoff: 1s → 2s → 4s → 8s (ليس linear)
  // 2. Jitter: إضافة عشوائية لتجنب "thundering herd" problem
  // 3. Max Delay Cap: حد أقصى للانتظار (30 ثانية)
  // 4. Timeout: حد أقصى للوقت الإجمالي (60 ثانية)
  // 5. Retryable Errors Only: إعادة المحاولة فقط للأخطاء المؤقتة
  //
  // Parameters:
  // - maxRetries: 3 محاولات (افتراضي)
  // - initialDelay: 1 ثانية (الانتظار الأول)
  // - backoffFactor: 2 (مضاعف exponential)
  // - maxDelay: 30 ثانية (حد أقصى للانتظار)
  // - timeout: 60 ثانية (حد أقصى للوقت الإجمالي)
  private async sendWithRetry(
    payload: CanonicalEventPayload, 
    maxRetries: number = 3,
    initialDelay: number = 1000, // 1 second
    backoffFactor: number = 2,    // exponential: 1s → 2s → 4s
    maxDelay: number = 30000,     // 30 seconds cap
    timeout: number = 60000       // 60 seconds total timeout
  ): Promise<boolean> {
    const startTime = Date.now();
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      // ✅ Check timeout: إذا تجاوز الوقت الإجمالي، توقف
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime >= timeout) {
        return false;
      }
      
      try {
        const result = await this.send(payload);
        
        // ✅ Best Practice: send() returns:
        // - true: success (event sent successfully)
        // - false: retryable error occurred (will retry)
        // - throws error: non-retryable error (will stop retry immediately)
        if (result) {
          return true;
        }
        
        // ✅ If result is false, it's a retryable error - continue to next attempt
      } catch (sendError: any) {
        // ✅ Best Practice: Non-retryable errors are thrown as exceptions
        // Check if error is marked as non-retryable
        if (sendError?.isRetryable === false) {
          return false; // Stop retry immediately for non-retryable errors
        }
        // Continue to retry logic below (treat as retryable error)
      }
      
      // ✅ Wait before retry (Exponential Backoff + Jitter)
      if (attempt < maxRetries) {
        // Exponential backoff: initialDelay * (backoffFactor ^ (attempt - 1))
        // Attempt 1: 1s * (2^0) = 1s
        // Attempt 2: 1s * (2^1) = 2s
        // Attempt 3: 1s * (2^2) = 4s
        const baseDelay = initialDelay * Math.pow(backoffFactor, attempt - 1);
        
        // ✅ Apply max delay cap
        const cappedDelay = Math.min(baseDelay, maxDelay);
        
        // ✅ Add jitter (±25% randomness) to avoid thundering herd problem
        // Jitter helps prevent many clients from retrying at the same time
        const jitter = cappedDelay * 0.25 * (Math.random() * 2 - 1); // -25% to +25%
        const waitTime = Math.max(100, Math.floor(cappedDelay + jitter)); // Minimum 100ms
        
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
    
    return false;
  }

  private async send(payload: CanonicalEventPayload): Promise<boolean> {
    const endpoint = this.cfg.signerEndpoint || this.cfg.endpoint;
    
    try {
      const allCookies = this.getAllCookies();
      
      if (!payload.enhanced_data) {
        payload.enhanced_data = {};
      }
      if (!payload.enhanced_data.server_side_cookies) {
        payload.enhanced_data.server_side_cookies = {};
      }
      
      Object.assign(payload.enhanced_data.server_side_cookies, allCookies);
      
      if (!payload.client_ids) {
        payload.client_ids = {};
      }
      
      if (allCookies['_scid'] && !allCookies['_sctr']) {
        const sctrDirect = getCookie('_sctr');
        if (sctrDirect) {
          allCookies['_sctr'] = sctrDirect;
          payload.enhanced_data.server_side_cookies['_sctr'] = sctrDirect;
        }
        
        const scCbtsDirect = getCookie('_ScCbts');
        if (scCbtsDirect && !allCookies['_ScCbts']) {
          allCookies['_ScCbts'] = scCbtsDirect;
          payload.enhanced_data.server_side_cookies['_ScCbts'] = scCbtsDirect;
        }
      }
      
      if (!payload.client_ids.scid && allCookies['_scid']) {
        payload.client_ids.scid = allCookies['_scid'];
      }
      if (!payload.client_ids.sctr && allCookies['_sctr']) {
        payload.client_ids.sctr = allCookies['_sctr'];
      }
      if (!payload.client_ids.sc_cbts && allCookies['_ScCbts']) {
        payload.client_ids.sc_cbts = allCookies['_ScCbts'];
      }

      const freshIds = this.getClientIds();
      for (const [k, v] of Object.entries(freshIds)) {
        if (v !== undefined && v !== '') {
          (payload.client_ids as Record<string, unknown>)[k] = v;
        }
      }
      
      // ✅ إضافة Ray ID إلى client_data إذا كان متوفراً من طلب سابق
      // Ray ID يأتي من Cloudflare (CF-Ray) أو Railway (X-Railway-Request-Id) في Response Headers
      // ✅ Fallback: قراءة من cookies إذا لم يكن موجوداً في الذاكرة
      if (!payload.client_data) {
        payload.client_data = {};
      }
      
      // ✅ الأولوية: Ray ID من الذاكرة (cachedRayID)
      // ✅ Fallback: Ray ID من cookies (cf_ray_id)
      // ⚠️ مهم: نتحقق من أن Ray ID من cookies ليس Railway Request ID (يجب أن يبدأ بـ Cloudflare format)
      let rayIDToUse = this.cachedRayID;
      if (!rayIDToUse) {
        const cookieRayID = getCookie('cf_ray_id');
        if (cookieRayID) {
          // ✅ التحقق من أن Ray ID من cookies هو Cloudflare Ray ID وليس Railway Request ID
          // Cloudflare Ray ID format: xxxxx-XXX (مثل: 9c18ba1a6cbea987-DMM)
          // Railway Request ID format: عادة أطول ولا يحتوي على -XXX في النهاية
          const isCloudflareFormat = /^[a-f0-9]+-[A-Z]{3}$/i.test(cookieRayID);
          if (isCloudflareFormat || cookieRayID.length > 15) {
            // يبدو أنه Cloudflare Ray ID
            rayIDToUse = cookieRayID;
            this.cachedRayID = cookieRayID; // تحديث الذاكرة من cookies
          }
        }
      }
      
      if (rayIDToUse) {
        payload.client_data.ray_id = rayIDToUse;
      }

      // مهلة لكل طلب: بدونها قد يبقى fetch معلّقاً دقائق ويؤخر "Finish" في DevTools ويشغّل إعادة محاولات بلا جدوى
      const TRACK_FETCH_TIMEOUT_MS = 14_000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TRACK_FETCH_TIMEOUT_MS);
      let res: Response;
      try {
        res = await fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit', // تغيير من 'include' لتوافق CORS مع AllowOrigins: "*"
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          keepalive: true,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
      
      // ✅ استخراج Ray ID من Response Headers (أفضل الممارسات)
      // الأولوية: Cloudflare CF-Ray (المفضل)
      // البديل: Railway X-Railway-Request-Id (إذا لم يكن Cloudflare متوفراً)
      // ملاحظة: بعد إضافة ExposeHeaders في CORS، يمكن قراءة CF-Ray مباشرة من Response Headers
      let cloudflareRayID: string | null = null;
      let railwayRequestID: string | null = null;
      
      try {
        // ✅ محاولة قراءة CF-Ray header بجميع الحالات الممكنة
        // ملاحظة: Headers في JavaScript case-insensitive، لكن نتحقق من جميع الحالات للتأكد
        cloudflareRayID = res.headers.get('CF-Ray') || 
                          res.headers.get('cf-ray') || 
                          res.headers.get('Cf-Ray') ||
                          res.headers.get('CF-RAY');
        
        // ✅ محاولة قراءة Railway Request ID كـ fallback فقط
        railwayRequestID = res.headers.get('X-Railway-Request-Id') || 
                          res.headers.get('x-railway-request-id');
        
      } catch (error) {
        // Failed to read response headers
      }
      
      // ✅ حفظ Ray ID من Headers (الأولوية الأولى)
      // ⚠️ مهم: نستخدم Cloudflare Ray ID فقط، ولا نستخدم Railway Request ID إلا إذا لم يكن Cloudflare متوفراً تماماً
      if (cloudflareRayID) {
        // حفظ Cloudflare Ray ID في الذاكرة والكوكيز للاستخدام في الطلبات التالية
        this.cachedRayID = cloudflareRayID;
        // ✅ حفظ في cookies أيضاً لضمان استمراريته حتى بعد إعادة تحميل الصفحة
        setCookie('cf_ray_id', cloudflareRayID, 1); // حفظ لمدة يوم واحد
      }
      
      // ✅ Fallback: محاولة استخراج ray_id من Response Body (إذا لم يكن موجوداً في Headers)
      // هذا حل بديل إذا كانت Response Headers غير قابلة للقراءة أو لم يكن Ray ID موجوداً
      // ⚠️ مهم: يجب قراءة Response Body قبل استهلاكه في res.ok check
      if (!this.cachedRayID) {
        try {
          // ✅ Clone response لقراءة Body بدون استهلاكه
          const responseText = await res.clone().text().catch(() => '');
          if (responseText) {
            try {
              const responseJson = JSON.parse(responseText);
              if (responseJson.ray_id && typeof responseJson.ray_id === 'string') {
                // ✅ حفظ ray_id من Response Body (هذا يجب أن يكون Cloudflare Ray ID من Backend)
                // نستخدم Response Body كـ fallback فقط إذا لم يكن موجوداً في Headers
                if (!this.cachedRayID) {
                  this.cachedRayID = responseJson.ray_id;
                  // ✅ حفظ في cookies أيضاً
                  setCookie('cf_ray_id', responseJson.ray_id, 1);
                }
              }
            } catch (e) {
              // Response ليس JSON، تجاهل
            }
          }
        } catch (e) {
          // فشل قراءة Response Body، تجاهل
        }
      }
      
      // ✅ إذا لم يكن Ray ID موجوداً بعد (لا من Headers ولا من Body)، نستخدم Railway Request ID كـ آخر fallback
      if (!this.cachedRayID && railwayRequestID) {
        // ⚠️ فقط إذا لم يكن Cloudflare متوفراً تماماً، نستخدم Railway Request ID
        this.cachedRayID = railwayRequestID;
        setCookie('cf_ray_id', railwayRequestID, 1);
      }
      
      if (!res.ok) {
        // ✅ Best Practice: Classify errors (retryable vs non-retryable)
        // Retryable errors: 408 (timeout), 429 (rate limit), 500-599 (server errors)
        // Non-retryable errors: 400-499 (client errors) - لا نعيد المحاولة
        const isRetryable = res.status === 408 || res.status === 429 || (res.status >= 500 && res.status < 600);
        
        // ✅ Best Practice: Return false for all errors (retryable and non-retryable)
        // sendWithRetry will check error type and decide whether to retry
        // We need to track error type separately to know if we should retry
        // For now, we throw an error with a flag to indicate retryability
        if (!isRetryable) {
          // Non-retryable error: throw error to stop retry immediately
          const error = new Error(`Non-retryable error: ${res.status}`);
          (error as any).isRetryable = false;
          (error as any).status = res.status;
          throw error;
        }
        
        // Retryable error: return false to trigger retry
        return false;
      }
      
      return true;
      
    } catch (sendError: unknown) {
      const errorMessage = sendError instanceof Error ? sendError.message : String(sendError);
      const errorName = sendError instanceof Error ? sendError.name : '';

      // إلغاء المهلة: لا فائدة من 3 محاولات متتالية بنفس الصفحة
      if (
        errorName === 'AbortError' ||
        (typeof DOMException !== 'undefined' &&
          sendError instanceof DOMException &&
          sendError.name === 'AbortError')
      ) {
        const err = new Error('Event hub request timeout');
        (err as { isRetryable?: boolean }).isRetryable = false;
        throw err;
      }

      // CORS / حظر الشبكة / امتدادات المتصفح: إعادة المحاولة لا تصلح الإعدادات — أوقف فوراً بدون انتظار backoff
      const isNonRetryableClient =
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('Load failed') ||
        errorMessage.includes('CORS') ||
        errorMessage.includes('NetworkError');

      if (isNonRetryableClient) {
        const err = new Error(errorMessage);
        (err as { isRetryable?: boolean }).isRetryable = false;
        throw err;
      }

      // أخطاء شبكة قد تكون مؤقتة (DNS، إلخ)
      const isMaybeTransient =
        errorMessage.includes('timeout') ||
        errorMessage.includes('ECONNREFUSED') ||
        errorMessage.includes('ENOTFOUND');

      if (isMaybeTransient) {
        return false;
      }
      
      // ✅ Other errors (parsing, validation) are non-retryable
      // Throw error to stop retry immediately (non-retryable error - no point retrying)
      const nonRetryableError = new Error(`Non-retryable error: ${errorMessage}`);
      (nonRetryableError as any).isRetryable = false;
      throw nonRetryableError;
    }
  }

  // ✅ إرسال الحدث إلى Meta Pixel SDK
  // يتم إرسال Page view هنا مع eventID للـ deduplication مع CAPI
  // ✅ الحل 2: تحسين معالجة الأخطاء - إرجاع boolean للإشارة إلى نجاح الإرسال
  // ✅ إصلاح: إضافة fallback mechanism للانتظار حتى يتم تحميل Meta Pixel SDK
  private async sendToMetaPixel(eventName: string, eventId: string, customData?: Record<string, any>): Promise<boolean> {
    if (typeof window === 'undefined') {
      return false;
    }

    // ✅ دالة مساعدة للتحقق من جاهزية Meta Pixel SDK
    const isMetaPixelReady = (): boolean => {
      return typeof window !== 'undefined' && typeof (window as any).fbq === 'function';
    };

    // ✅ Fallback: انتظار تحميل Meta Pixel SDK (مهم للـ deduplication)
    if (!isMetaPixelReady()) {
      // ✅ انتظار تحميل Meta Pixel SDK (حتى 15 ثواني)
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 30; // 30 محاولة × 500ms = 15 ثواني
        let resolved = false;
        
        // ✅ Listener للاستجابة الفورية عند جاهزية Meta Pixel
        const onMetaPixelReady = () => {
          if (!resolved && isMetaPixelReady()) {
            resolved = true;
            if (typeof window !== 'undefined') {
              window.removeEventListener('metaPixelReady', onMetaPixelReady);
            }
            clearInterval(interval);
            resolve(this.sendToMetaPixelInternal(eventName, eventId, customData));
          }
        };
        
        // ✅ إضافة event listener للاستجابة الفورية
        if (typeof window !== 'undefined' && window.addEventListener) {
          window.addEventListener('metaPixelReady', onMetaPixelReady);
        }
        
        const interval = setInterval(() => {
          attempts++;
          if (isMetaPixelReady()) {
            if (!resolved) {
              resolved = true;
              if (typeof window !== 'undefined') {
                window.removeEventListener('metaPixelReady', onMetaPixelReady);
              }
              clearInterval(interval);
              resolve(this.sendToMetaPixelInternal(eventName, eventId, customData));
            }
          } else if (attempts >= maxAttempts) {
            if (!resolved) {
              resolved = true;
              if (typeof window !== 'undefined') {
                window.removeEventListener('metaPixelReady', onMetaPixelReady);
              }
              clearInterval(interval);
              resolve(false);
            }
          }
        }, 500);
      });
    }

    return this.sendToMetaPixelInternal(eventName, eventId, customData);
  }

  // ✅ دالة منفصلة لإرسال الحدث (بعد التأكد من تحميل SDK)
  private sendToMetaPixelInternal(eventName: string, eventId: string, customData?: Record<string, any>): boolean {
    if (typeof window === 'undefined' || typeof (window as any).fbq !== 'function') {
      return false;
    }

    // ✅ Validation: التأكد من أن eventId موجود (مطلوب للـ deduplication)
    // event_id ضروري لضمان عدم عد نفس الحدث مرتين (Pixel + CAPI)
    if (!eventId || eventId.trim() === '') {
      return false;
    }

    try {
      const fbq = (window as any).fbq;
      
      const normalizedEventName = normalizeEventName(eventName);
      const metaEventName = mapMeta[normalizedEventName] || normalizedEventName;
      
      const eventData: any = {};
      
      if (customData) {
        if (customData.currency) eventData.currency = customData.currency;
        if (customData.value) eventData.value = customData.value;
        if (customData.content_name) eventData.content_name = customData.content_name;
        if (customData.content_category) eventData.content_category = customData.content_category;
        if (customData.content_ids) eventData.content_ids = customData.content_ids;
        if (customData.content_type) eventData.content_type = customData.content_type;
        
        if (customData.items && customData.items.length > 0) {
          eventData.contents = customData.items.map((item: any) => ({
            id: item.item_id || item.id,
            quantity: item.quantity || 1,
            item_price: item.price || item.item_price || 0
          }));
          
          eventData.content_ids = customData.items.map((item: any) => 
            item.item_id || item.id
          );
          
          if (customData.items[0].item_name) {
            eventData.content_name = customData.items[0].item_name;
          }
          if (customData.items[0].item_category) {
            eventData.content_category = customData.items[0].item_category;
          }
        }
        
        if (customData.transaction_id) {
          eventData.order_id = customData.transaction_id;
        }
        
        // ✅ إضافة الحقول المفقودة للتطابق الكامل مع CAPI
        // Description
        if (customData.content_name) {
          eventData.description = customData.content_name;
        } else if (customData.items && customData.items.length > 0 && customData.items[0].name) {
          eventData.description = customData.items[0].name;
        } else if (customData.items && customData.items.length > 0 && customData.items[0].item_name) {
          eventData.description = customData.items[0].item_name;
        }
        
        // Search String
        if (customData.search_term) {
          eventData.search_string = customData.search_term;
        }
      }
      
      // Payment Info Available
      const paymentEvents = ['ADD_PAYMENT_INFO', 'PURCHASE', 'INITIATE_CHECKOUT', 'START_CHECKOUT'];
      if (paymentEvents.includes(normalizedEventName)) {
        eventData.payment_info_available = true;
      }
      
      // Sign-up Method
      const signUpEvents = ['COMPLETE_REGISTRATION', 'LEAD', 'IDENTIFY', 'SIGN_UP'];
      if (signUpEvents.includes(normalizedEventName)) {
        eventData.sign_up_method = customData?.search_term ? 'search' : 'website';
      }
      
      // Success
      const successEvents = ['PURCHASE', 'COMPLETE_REGISTRATION', 'LEAD', 'IDENTIFY'];
      eventData.success = successEvents.includes(normalizedEventName);
      
      // ✅ إضافة fbp يدوياً للتطابق الكامل مع CAPI (مهم للـ deduplication)
      // حسب وثائق Facebook: fbp يمكن استخدامه كبديل للـ deduplication إذا لم يكن event_id متوفر
      // Meta SDK يرسل fbp تلقائياً في user_data، لكن إضافته يدوياً في eventData يضمن التطابق
      // هذا مهم جداً للـ deduplication بين Pixel و CAPI
      const fbp = createOrGetFbp();
      if (fbp) {
        // ✅ إضافة fbp في eventData للتطابق مع CAPI
        // ملاحظة: Meta SDK يرسل fbp تلقائياً في user_data، لكن إضافته يدوياً يضمن التطابق
        eventData.fbp = fbp;
      }
      
      // ✅ إضافة fbc للتطابق الكامل مع CAPI (مهم للـ Attribution)
      // fbc يحتوي على fbclid الذي يربط النقرة بالحدث
      const fbc = createOrGetFbc();
      if (fbc) {
        eventData.fbc = fbc;
      }
      
      // ✅ إضافة external_id للـ Advanced Matching (تحسين EMQ)
      // external_id يساعد Meta على مطابقة الأحداث للمستخدمين حتى بدون email/phone
      const externalId = this.cachedUser.user_id || this.anonId;
      if (externalId) {
        eventData.external_id = externalId;
      }
      
      // ✅ إضافة ctwa_clid للتتبع من إعلانات Click to WhatsApp
      // يربط التحويل بإعلان WhatsApp الأصلي
      const ctwaClid = createOrGetCtwaClid();
      if (ctwaClid) {
        eventData.ctwa_clid = ctwaClid;
      }
      
      // ✅ إرسال الحدث إلى Meta Pixel SDK
      // للـ Page view: metaEventName = 'PageView' (من mapMeta)
      // eventID ضروري للـ deduplication مع CAPI (نفس event_id يُرسل في CAPI أيضاً)
      fbq('track', metaEventName, eventData, {
        eventID: eventId
      });
      
      return true;
    } catch (error) {
      return false;
    }
  }

  // ✅ الحل 2: تحسين معالجة الأخطاء - إرجاع boolean للإشارة إلى نجاح الإرسال
  // ✅ إصلاح: إضافة fallback mechanism للانتظار حتى يتم تحميل Snapchat Pixel SDK
  private async sendToSnapchatPixel(
    eventName: string,
    eventId: string,
    customData?: Record<string, any>,
    enhancedData?: EHEnhancedData,
    user?: EHUser,
    clientIds?: EHClientIds
  ): Promise<boolean> {
    if (typeof window === 'undefined') {
      return false;
    }

    // ✅ دالة مساعدة للتحقق من جاهزية Snapchat Pixel SDK
    const isSnapchatPixelReady = (): boolean => {
      return typeof window !== 'undefined' && typeof (window as any).snaptr === 'function';
    };

    // ✅ Fallback: انتظار تحميل Snapchat Pixel SDK (مهم للـ deduplication)
    if (!isSnapchatPixelReady()) {
      // ✅ انتظار تحميل Snapchat Pixel SDK (حتى 15 ثواني)
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 30; // 30 محاولة × 500ms = 15 ثواني
        let resolved = false;
        
        // ✅ Listener للاستجابة الفورية عند جاهزية Snapchat Pixel
        const onSnapchatPixelReady = () => {
          if (!resolved && isSnapchatPixelReady()) {
            resolved = true;
            if (typeof window !== 'undefined') {
              window.removeEventListener('snapchatPixelReady', onSnapchatPixelReady);
            }
            clearInterval(interval);
            resolve(this.sendToSnapchatPixelInternal(eventName, eventId, customData, enhancedData, user, clientIds));
          }
        };
        
        // ✅ إضافة event listener للاستجابة الفورية
        if (typeof window !== 'undefined' && window.addEventListener) {
          window.addEventListener('snapchatPixelReady', onSnapchatPixelReady);
        }
        
        const interval = setInterval(() => {
          attempts++;
          if (isSnapchatPixelReady()) {
            if (!resolved) {
              resolved = true;
              if (typeof window !== 'undefined') {
                window.removeEventListener('snapchatPixelReady', onSnapchatPixelReady);
              }
              clearInterval(interval);
              resolve(this.sendToSnapchatPixelInternal(eventName, eventId, customData, enhancedData, user, clientIds));
            }
          } else if (attempts >= maxAttempts) {
            if (!resolved) {
              resolved = true;
              if (typeof window !== 'undefined') {
                window.removeEventListener('snapchatPixelReady', onSnapchatPixelReady);
              }
              clearInterval(interval);
              resolve(false);
            }
          }
        }, 500);
      });
    }

    return this.sendToSnapchatPixelInternal(eventName, eventId, customData, enhancedData, user);
  }

  // ✅ دالة منفصلة لإرسال الحدث (بعد التأكد من تحميل SDK)
  private sendToSnapchatPixelInternal(
    eventName: string,
    eventId: string,
    customData?: Record<string, any>,
    enhancedData?: EHEnhancedData,
    user?: EHUser,
    clientIds?: EHClientIds
  ): boolean {
    if (typeof window === 'undefined' || typeof (window as any).snaptr !== 'function') {
      return false;
    }

    try {
      const snaptr = (window as any).snaptr;

      const normalizedEventName = normalizeEventName(eventName);

      // Skip IDENTIFY events — same as CAPI (identity stitching only, not a conversion)
      if (normalizedEventName === 'IDENTIFY') {
        return false;
      }

      const snapEventName = this.mapToSnapchatEvent(normalizedEventName);
      
      const eventData: any = {};
      
      if (customData && customData.currency) {
        eventData.currency = customData.currency;
      } else {
        eventData.currency = 'SAR';
      }
      
      if (customData) {
        if (customData.value) eventData.price = customData.value;
        // Snapchat standard field = transaction_id (order_id is Meta convention)
        if (customData.transaction_id) eventData.transaction_id = customData.transaction_id;
        // ✅ إصلاح: item_ids قد تأتي كـ item_ids أو content_ids
        if (customData.item_ids) {
          eventData.item_ids = customData.item_ids;
        } else if (customData.content_ids) {
          eventData.item_ids = customData.content_ids;
        } else if (customData.items && customData.items.length > 0) {
          // استخراج item_ids من items array
          eventData.item_ids = customData.items.map((item: any) => item.id || item.item_id).filter(Boolean);
        }
        
        // ✅ number_items (فقط في START_CHECKOUT و PURCHASE)
        if (normalizedEventName === 'START_CHECKOUT' || normalizedEventName === 'PURCHASE' || normalizedEventName === 'INITIATE_CHECKOUT') {
          if (customData.number_items) {
            eventData.number_items = customData.number_items;
          } else if (customData.items && customData.items.length > 0) {
            // حساب number_items من items
            const totalQuantity = customData.items.reduce((sum: number, item: any) => {
              return sum + (item.quantity || 1);
            }, 0);
            if (totalQuantity > 0) {
              eventData.number_items = totalQuantity;
            }
          }
        }
        
        // ✅ إضافة الحقول المفقودة للتطابق الكامل مع CAPI
        // Description
        if (customData.content_name) {
          eventData.description = customData.content_name;
        } else if (customData.items && customData.items.length > 0 && customData.items[0].name) {
          eventData.description = customData.items[0].name;
        }
        
        // Item Category — align with CAPI (item_category on items or custom_data)
        if (customData.item_category) {
          eventData.item_category = customData.item_category;
        } else if (customData.items && customData.items.length > 0) {
          const firstItem = customData.items[0];
          if (firstItem.item_category) {
            eventData.item_category = firstItem.item_category;
          } else if (firstItem.category) {
            eventData.item_category = firstItem.category;
          }
        } else if (customData.category) {
          eventData.item_category = customData.category;
        }
        
        // Search String
        if (customData.search_term) {
          eventData.search_string = customData.search_term;
        }
      }
      
      // ✅ uuid_c1 / uuid_c2 / uuid_c3 — يطابق CAPI تماماً
      // المصدر الأساسي: client_ids المُلتقطة في track() (نفس البيانات المرسلة لـ CAPI)
      // Fallback: قراءة الكوكيز مباشرة لو لم تُمرَّر client_ids
      const scid = clientIds?.scid || getCookie('_scid');
      if (scid) {
        eventData.uuid_c1 = scid;
      }

      // _sctr يُحفظ URL-encoded (مثلاً "1%7C1768856400000")
      const sctrRaw = clientIds?.sctr || getCookie('_sctr');
      let sctrDecoded = '';
      if (sctrRaw) {
        try {
          sctrDecoded = decodeURIComponent(sctrRaw);
        } catch {
          sctrDecoded = sctrRaw;
        }
      }

      // uuid_c2 = الجزء قبل "|" من _sctr المفكوك (مطابق snapParsedSCTRPrefix في CAPI)
      if (sctrDecoded) {
        const pipeIdx = sctrDecoded.indexOf('|');
        const c2 = pipeIdx > 0 ? sctrDecoded.substring(0, pipeIdx) : sctrDecoded;
        if (c2) {
          eventData.uuid_c2 = c2;
        }
      }

      // uuid_c3 = _ScCbts أولاً، ثم _sctr المفكوك (مطابق snapCookie3ForUserData في CAPI)
      const scCbts = clientIds?.sc_cbts || getCookie('_ScCbts');
      if (scCbts) {
        eventData.uuid_c3 = scCbts;
      } else if (sctrDecoded) {
        eventData.uuid_c3 = sctrDecoded;
      }
      
      // ✅ customer_status
      eventData.customer_status = enhancedData?.is_returning_visitor ? 'returning' : 'new';
      
      // ✅ data_use
      eventData.data_use = 'advertising'; // افتراضي
      
      // ✅ event_tag
      eventData.event_tag = 'website_conversion';
      
      // Payment Info Available
      const paymentEvents = ['ADD_PAYMENT_INFO', 'PURCHASE', 'INITIATE_CHECKOUT', 'START_CHECKOUT'];
      if (paymentEvents.includes(normalizedEventName)) {
        eventData.payment_info_available = true;
      }
      
      // Sign-up Method
      const signUpEvents = ['COMPLETE_REGISTRATION', 'LEAD', 'IDENTIFY', 'SIGN_UP'];
      if (signUpEvents.includes(normalizedEventName)) {
        eventData.sign_up_method = customData?.search_term ? 'search' : 'website';
      }
      
      // Success — Snapchat expects 1/0 (aligned with CAPI)
      const successEvents = ['PURCHASE', 'COMPLETE_REGISTRATION', 'LEAD', 'IDENTIFY'];
      eventData.success = successEvents.includes(normalizedEventName) ? 1 : 0;
      
      if (enhancedData && enhancedData.device_type && enhancedData.device_type !== 'Unknown') {
        eventData.device_model = enhancedData.device_type;
      }
      
      if (enhancedData && enhancedData.operating_system && enhancedData.operating_system !== 'Unknown') {
        eventData.os_version = enhancedData.operating_system;
      }
      
      if (user && user.zip) {
        eventData.postcode = user.zip;
      }
      
      // ✅ user_email و user_phone_number (unhashed)
      if (user && user.email) {
        eventData.user_email = user.email;
      }
      if (user && user.phone) {
        eventData.user_phone_number = user.phone;
      }
      
      // ✅ user_hashed_email و user_hashed_phone_number
      // ملاحظة: Snapchat Pixel لا يحتاج hash يدوي - Snapchat SDK يقوم بذلك تلقائياً
      // لكن يمكن إضافته إذا كان مطلوباً
      if (user && user.email) {
        // Snapchat SDK يقوم بـ hash تلقائياً، لكن يمكن إضافة hashed version أيضاً
        // eventData.user_hashed_email = hashEmail(user.email);
      }
      if (user && user.phone) {
        // eventData.user_hashed_phone_number = hashPhone(user.phone);
      }
      
      // ✅ firstname و lastname (من الاسم الكامل)
      if (user && user.first_name) {
        eventData.firstname = user.first_name;
      }
      if (user && user.last_name) {
        eventData.lastname = user.last_name;
      }
      
      // ✅ geo_city, geo_country, geo_region
      // ملاحظة: هذه البيانات عادة تأتي من IP lookup في Backend
      // في Pixel، يمكن إضافتها إذا كانت متوفرة من enhanced_data
      // ملاحظة: EHEnhancedData لا يحتوي على geo_city/geo_country/geo_region
      // هذه البيانات تأتي من Backend فقط
      
      // ✅ client_dedup_id يجب أن يكون مفتاحاً داخل خصائص الحدث (الوسيط الثالث)
      // وليس وسيطاً رابعاً — Snap Pixel SDK يتجاهل الوسيط الرابع، مما يسقط مفتاح
      // إزالة التكرار ويسبب "Low Client Deduplication ID coverage" / "Missing deduplication key".
      // الشراء كان يعمل عبر transaction_id؛ الآن تحمل كل الأحداث client_dedup_id بشكل صحيح.
      if (eventId && eventId.trim() !== '') {
        eventData.client_dedup_id = eventId;
      }

      snaptr('track', snapEventName, eventData);
      
      return true;
    } catch (error) {
      return false;
    }
  }

  private mapToSnapchatEvent(eventName: string): string {
    // ⚠️ يجب أن تبقى مطابقة تماماً لـ normalizeSnapchatEventName في
    // event-hub/internal/destinations/snapchat.go — أي اختلاف يكسر الـ deduplication
    // (event_name مختلف بين Pixel و CAPI => عدّ مزدوج).
    const snapEventMap: Record<string, string> = {
      // القياسية
      'PAGE_VIEW': 'PAGE_VIEW',
      'VIEW_CONTENT': 'VIEW_CONTENT',
      'VIEW_ITEM_LIST': 'VIEW_CONTENT',
      'ADD_TO_CART': 'ADD_CART',
      'INITIATE_CHECKOUT': 'START_CHECKOUT',
      'ADD_PAYMENT_INFO': 'ADD_BILLING',
      'PURCHASE': 'PURCHASE',
      'COMPLETE_REGISTRATION': 'SIGN_UP',
      'IDENTIFY': 'SIGN_UP',
      'LOGIN': 'LOGIN',
      'LOGOUT': 'PAGE_VIEW',
      'SEARCH': 'SEARCH',
      'LEAD': 'SIGN_UP',
      'CONTACT': 'CUSTOM_EVENT_1',
      // غير القياسية — لمطابقة الخادم (كانت تسقط إلى CUSTOM_EVENT_1)
      'SUBSCRIBE': 'SUBSCRIBE',
      'SHARE': 'SHARE',
      'SAVE': 'SAVE',
      'ADD_TO_WISHLIST': 'ADD_TO_WISHLIST',
      'SPENT_CREDITS': 'SPENT_CREDITS',
      'RATE': 'RATE',
      'START_TRIAL': 'START_TRIAL',
      'LIST_VIEW': 'LIST_VIEW',
      'COMPLETE_TUTORIAL': 'COMPLETE_TUTORIAL',
      'LEVEL_COMPLETE': 'LEVEL_COMPLETE',
      'ACHIEVEMENT_UNLOCKED': 'ACHIEVEMENT_UNLOCKED',
      'APP_INSTALL': 'APP_INSTALL',
      'APP_OPEN': 'APP_OPEN',
      'AD_CLICK': 'AD_CLICK',
      'AD_VIEW': 'AD_VIEW',
      'INVITE': 'INVITE',
      'RESERVE': 'RESERVE',
    };

    return snapEventMap[eventName] || 'CUSTOM_EVENT_1';
  }

  // ✅ الحل 2: تحسين معالجة الأخطاء - إرجاع boolean للإشارة إلى نجاح الإرسال
  // ✅ Best Practice: Send customer information with every web event (EMQ improvement)
  // ✅ إصلاح: إضافة fallback mechanism للانتظار حتى يتم تحميل TikTok Pixel SDK
  private async sendToTikTokPixel(eventName: string, eventId: string, customData?: Record<string, any>, user?: EHUser): Promise<boolean> {
    // ✅ التحقق من وجود TikTok Pixel SDK مع fallback mechanism
    if (typeof window === 'undefined') {
      return false;
    }

    // ✅ دالة مساعدة للتحقق من جاهزية TikTok Pixel SDK
    // ⚠️ إصلاح: ttq قد يكون function أو object (حسب TikTok Pixel SDK)
    const isTikTokPixelReady = (): boolean => {
      if (typeof window === 'undefined') return false;
      const ttq = (window as any).ttq;
      
      // ✅ التحقق من أن ttq موجود (function أو object)
      if (typeof ttq === 'undefined' || ttq === null) {
        return false;
      }
      
      // ✅ التحقق من أن ttq.track متاح (مهم جداً)
      // TikTok Pixel SDK قد يكون function أو object، لكن track يجب أن يكون function
      const hasTrack = typeof ttq.track === 'function';
      
      // ✅ التحقق من أن ttq ليس مجرد function فارغة
      // يجب أن يكون object مع methods أو function مع track method
      const isValid = (typeof ttq === 'object' || typeof ttq === 'function') && hasTrack;
      
      return isValid;
    };

    // ✅ Fallback: انتظار تحميل TikTok Pixel SDK (مهم للـ deduplication)
    if (!isTikTokPixelReady()) {
      // ✅ انتظار تحميل TikTok Pixel SDK (حتى 15 ثواني - زيادة الوقت)
      // ✅ إضافة listener لـ custom event 'tiktokPixelReady' للاستجابة الفورية
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 30; // 30 محاولة × 500ms = 15 ثواني (زيادة من 10 ثواني)
        let resolved = false;
        
        // ✅ Listener للاستجابة الفورية عند جاهزية TikTok Pixel
        const onTikTokPixelReady = () => {
          if (!resolved && isTikTokPixelReady()) {
            resolved = true;
            if (typeof window !== 'undefined') {
              window.removeEventListener('tiktokPixelReady', onTikTokPixelReady);
            }
            clearInterval(interval);
            resolve(this.sendToTikTokPixelInternal(eventName, eventId, customData, user));
          }
        };
        
        // ✅ إضافة event listener للاستجابة الفورية
        if (typeof window !== 'undefined' && window.addEventListener) {
          window.addEventListener('tiktokPixelReady', onTikTokPixelReady);
        }
        
        const interval = setInterval(() => {
          attempts++;
          // ✅ التحقق من الجاهزية (بدون logging مفرط)
          if (isTikTokPixelReady()) {
            if (!resolved) {
              resolved = true;
              if (typeof window !== 'undefined') {
                window.removeEventListener('tiktokPixelReady', onTikTokPixelReady);
              }
              clearInterval(interval);
              // ✅ إرسال الحدث بعد تحميل SDK
              resolve(this.sendToTikTokPixelInternal(eventName, eventId, customData, user));
            }
          } else if (attempts >= maxAttempts) {
            if (!resolved) {
              resolved = true;
              if (typeof window !== 'undefined') {
                window.removeEventListener('tiktokPixelReady', onTikTokPixelReady);
              }
              clearInterval(interval);
              resolve(false);
            }
          }
        }, 500); // محاولة كل 500ms
      });
    }

    // ✅ TikTok Pixel SDK جاهز - إرسال الحدث مباشرة
    return this.sendToTikTokPixelInternal(eventName, eventId, customData, user);
  }

  // ✅ دالة منفصلة لإرسال الحدث (بعد التأكد من تحميل SDK)
  private async sendToTikTokPixelInternal(eventName: string, eventId: string, customData?: Record<string, any>, user?: EHUser): Promise<boolean> {
    // ✅ التحقق مرة أخرى من وجود TikTok Pixel SDK و track function
    if (typeof window === 'undefined') {
      return false;
    }

    const ttq = (window as any).ttq;
    // ✅ إصلاح: التحقق من function أو object (ليس فقط object)
    if (typeof ttq === 'undefined' || ttq === null || typeof ttq.track !== 'function') {
      return false;
    }

    try {
      const ttq = (window as any).ttq;
      
      const normalizedEventName = normalizeEventName(eventName);
      
      const tiktokEventName = this.mapToTikTokEvent(normalizedEventName);
      
      const properties: any = {};
      
      if (customData) {
        if (customData.currency) properties.currency = customData.currency;
        // ✅ إصلاح: إرسال value فقط إذا كانت > 0 (TikTok يعتبر value:0 كـ "not valid")
        // هذا يحل مشكلة "Purchase value is not valid" (64.89%)
        if (customData.value !== undefined && customData.value !== null) {
          const value = Number(customData.value);
          // TikTok يتطلب value أن يكون: number, > 0, finite, <= 999999999.99
          const isValidValue = !isNaN(value) && isFinite(value) && value > 0 && value <= 999999999.99;
          
          if (isValidValue) {
            properties.value = value;
          }
        }
        // ⚠️ لا نضيف value: 0 للأحداث التجارية - TikTok يعتبرها "not valid"
        if (customData.content_type) properties.content_type = customData.content_type;
        
        if (customData.items && customData.items.length > 0) {
          properties.content_id = customData.items[0].id;
        } else if (customData.content_ids && customData.content_ids.length > 0) {
          properties.content_id = customData.content_ids[0];
        } else if (customData.content_id) {
          properties.content_id = customData.content_id;
        }
        
      if (customData.content_name) properties.content_name = customData.content_name;
      if (customData.content_category) properties.content_category = customData.content_category;
      if (customData.quantity) properties.quantity = customData.quantity;
      if (customData.description) properties.description = customData.description;
      if (customData.query) properties.query = customData.query;
      
      // ✅ إضافة order_id للتطابق الكامل مع Events API
      if (customData.transaction_id) {
        properties.order_id = customData.transaction_id;
      }
      
      // ✅ إضافة الحقول المفقودة للتطابق الكامل مع Events API
      // Payment Info Available
      const paymentEvents = ['ADD_PAYMENT_INFO', 'PURCHASE', 'INITIATE_CHECKOUT', 'START_CHECKOUT'];
      if (paymentEvents.includes(normalizedEventName)) {
        properties.payment_info_available = true;
      } else {
        properties.payment_info_available = false;
      }
      
      // Sign-up Method
      const signUpEvents = ['COMPLETE_REGISTRATION', 'LEAD', 'IDENTIFY', 'SIGN_UP'];
      if (signUpEvents.includes(normalizedEventName)) {
        properties.sign_up_method = customData?.search_term ? 'search' : 'website';
      }
      
      // Success
      const successEvents = ['PURCHASE', 'COMPLETE_REGISTRATION', 'LEAD', 'IDENTIFY'];
      properties.success = successEvents.includes(normalizedEventName);
        
        if (customData.items && customData.items.length > 0) {
          properties.contents = customData.items.map((item: any) => ({
            content_id: item.id,
            content_name: item.name,
            content_category: item.category,
            quantity: item.quantity || 1,
            price: item.price
          }));
        }
      }
      
      if (tiktokEventName === 'ViewContent' && !properties.content_id) {
        properties.content_id = eventId;
        if (!properties.content_name) {
          properties.content_name = 'Page View';
        }
        if (!properties.contents) {
          properties.contents = [{
            content_id: eventId,
            quantity: 1,
            currency: properties.currency || 'SAR'
          }];
        }
      }
      
      // ✅ Best Practice: Include customer information with every event (EMQ improvement)
      // Reference: TikTok Event Match Quality best practices
      // This improves match rate by 33% and reduces CPA by 30%
      // Note: TikTok Pixel SDK automatically hashes PII, so we send raw values
      const userToUse = user || this.cachedUser;
      if (userToUse) {
        // ✅ Send email if available (TikTok SDK will hash automatically)
        // Best Practice: Send customer information with every web event
        if (userToUse.email) {
          // TikTok Pixel SDK automatically hashes email, so send raw value
          properties.email = userToUse.email;
        }
        
        // ✅ Send phone if available (TikTok SDK will hash automatically)
        // Best Practice: Send customer information with every web event
        if (userToUse.phone) {
          // TikTok Pixel SDK automatically hashes phone, so send raw value
          properties.phone = userToUse.phone;
        }
        
        // ✅ Send external_id for Advanced Matching (EMQ improvement)
        // external_id helps TikTok match events to users across devices
        // Use user_id if available, otherwise use anon_id
        const externalId = userToUse.user_id || this.anonId;
        if (externalId) {
          properties.external_id = externalId;
        }
      } else {
        // ✅ Even without user, send external_id using anon_id
        // This ensures every event has an external_id for matching
        if (this.anonId) {
          properties.external_id = this.anonId;
        }
      }
      
      try {
        // ✅ إصلاح Event ID mismatch: إضافة event_id داخل properties (وليس في options)
        // حسب وثائق TikTok الرسمية، event_id يجب أن يكون في properties للـ deduplication
        // Reference: https://ads.tiktok.com/help/article/set-up-events-api
        if (eventId && eventId.trim() !== '') {
          const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(eventId);
          if (isValidUUID) {
            properties.event_id = eventId; // ✅ إضافة event_id داخل properties
          }
        }
        
        // ✅ إرسال الحدث مع event_id في properties
        ttq.track(tiktokEventName, properties);
      } catch (trackError) {
        throw trackError;
      }
      
      return true;
    } catch (error) {
      return false;
    }
  }

  private mapToTikTokEvent(eventName: string): string {
    // يجب أن يبقى متطابقاً مع mapEventNameForTikTok في event-hub (tiktok.go)
    const tiktokEventMap: Record<string, string> = {
      'PAGE_VIEW': 'PageView',
      'VIEW_CONTENT': 'ViewContent',
      'VIEW_ITEM_LIST': 'ViewContent',
      'CONTACT': 'Contact',
      'SEARCH': 'Search',
      
      'ADD_TO_CART': 'AddToCart',
      'INITIATE_CHECKOUT': 'InitiateCheckout',
      'ADD_PAYMENT_INFO': 'AddPaymentInfo',
      'PURCHASE': 'Purchase', // ✅ Standard Event name according to TikTok documentation
      
      'COMPLETE_REGISTRATION': 'CompleteRegistration',
      'IDENTIFY': 'CompleteRegistration',
      'LOGIN': 'CompleteRegistration',
      'LEAD': 'CompleteRegistration',
      
      'SUBSCRIBE': 'Subscribe',
      'DOWNLOAD': 'Download',
      'CLICK_BUTTON': 'ClickButton',
      'SUBMIT_FORM': 'SubmitForm',
      'FORM_INTERACTION': 'SubmitForm',
      'ENGAGEMENT': 'Engagement',
      
      'LOGOUT': 'PageView',
    };
    
    return tiktokEventMap[eventName] || 'CompleteRegistration';
  }

  // ✅ الحل 2: تحسين معالجة الأخطاء - إرجاع boolean للإشارة إلى نجاح الإرسال
  private sendToYandexPixel(eventName: string, eventId: string, customData?: Record<string, any>): boolean {
    if (typeof window === 'undefined' || typeof (window as any).ym !== 'function') {
      return false;
    }

    try {
      const ym = (window as any).ym;
      const yandexCounterId = import.meta.env.VITE_YANDEX_COUNTER_ID;
      
      if (!yandexCounterId) {
        return false;
      }

      // eventName هنا هو الاسم الموحّد (normalizeEventName) — يطابق event-hub وليس EHEventName
      const ecommerceEvents = [
        'ADD_TO_CART',
        'PURCHASE',
        'INITIATE_CHECKOUT',
        'ADD_PAYMENT_INFO',
      ];

      if (!ecommerceEvents.includes(eventName)) {
        return false;
      }

      const currencyCode = customData?.currency || 'SAR';

      const products = customData?.items?.map((item: any) => ({
        id: item.item_id || item.id,
        name: item.item_name || item.name,
        price: item.price || item.item_price || 0,
        quantity: item.quantity || 1,
        category: item.item_category || item.category || 'General'
      })) || [];

      // Yandex Metrica ecommerce يدعم فقط: impressions, click, detail, add, remove, purchase
      // (لا يوجد checkout/checkout_option — تلك مفاهيم Universal Analytics فقط ويتجاهلها Yandex)
      // ملاحظة: إجراء add يجب أن يكون { products: [...] } وليس مصفوفة مباشرة.
      let ecommerce: Record<string, any> | null = null;
      if (eventName === 'ADD_TO_CART') {
        ecommerce = { currencyCode, add: { products } };
      } else if (eventName === 'PURCHASE') {
        ecommerce = {
          currencyCode,
          purchase: {
            actionField: {
              id: customData?.transaction_id || eventId,
              // revenue اختياري؛ يُحسب تلقائياً من أسعار المنتجات إن غاب. order_price غير قياسي فأُزيل.
              ...(customData?.value != null ? { revenue: customData.value } : {}),
            },
            products,
          },
        };
      }

      // الآلية الرسمية الوحيدة لإرسال ecommerce في Yandex هي الـ dataLayer
      // (window.dataLayer يُنشأ مسبقاً في PixelLoader قبل تهيئة العداد — هنا نكتفي بالـ push دون إعادة تعيين)
      if (ecommerce) {
        const dl = (window as any).dataLayer || ((window as any).dataLayer = []);
        try {
          dl.push({ ecommerce });
        } catch {
          // dataLayer push فشل — تجاهل هادئ
        }
      }

      // أحداث القمع التي لا يمثّلها نموذج ecommerce في Yandex تُتابَع كأهداف JS (reachGoal)
      // (الأهداف يجب تعريفها في لوحة Metrica؛ الاستدعاء غير ضار إن لم تُعرَّف بعد)
      try {
        if (eventName === 'PURCHASE') {
          ym(Number(yandexCounterId), 'reachGoal', 'purchase', {
            order_price: customData?.value || 0,
            currency: currencyCode,
            order_id: customData?.transaction_id || eventId,
          });
        } else if (eventName === 'INITIATE_CHECKOUT') {
          ym(Number(yandexCounterId), 'reachGoal', 'begin_checkout');
        } else if (eventName === 'ADD_PAYMENT_INFO') {
          ym(Number(yandexCounterId), 'reachGoal', 'add_payment_info');
        }
      } catch {
        // reachGoal فشل — تجاهل هادئ
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  // ✅ إرسال الأحداث إلى GA4 عبر gtag
  private sendToGA4(eventName: string, eventId: string, customData?: Record<string, any>): boolean {
    if (this.consent.analytics_storage === false) {
      return false;
    }
    if (typeof window === 'undefined' || typeof (window as any).gtag !== 'function') {
      return false;
    }

    const ga4Id = ga4MeasurementId();
    if (!ga4Id) {
      return false;
    }

    try {
      configureGa4(ga4Id);
      const gtag = (window as any).gtag;
      const normalizedEventName = normalizeEventName(eventName);
      const ga4EventName = mapGA4[normalizedEventName] || normalizedEventName.toLowerCase();
      
      // بناء parameters الحدث
      const eventParams: Record<string, any> = {
        event_id: eventId, // ✅ للـ deduplication مع Measurement Protocol
      };

      // إضافة البيانات المشتركة
      if (customData) {
        if (customData.currency) eventParams.currency = customData.currency;
        if (customData.value) eventParams.value = customData.value;
        if (customData.transaction_id) eventParams.transaction_id = customData.transaction_id;
        
        // إضافة items بتنسيق GA4
        if (customData.items && customData.items.length > 0) {
          eventParams.items = customData.items.map((item: any) => ({
            item_id: item.item_id || item.id,
            item_name: item.item_name || item.name,
            price: item.price || item.item_price || 0,
            quantity: item.quantity || 1,
            item_category: item.item_category || item.category || 'General',
          }));
        }
        
        // إضافة content parameters
        if (customData.content_name) eventParams.content_name = customData.content_name;
        if (customData.content_type) eventParams.content_type = customData.content_type;
        if (customData.content_category) eventParams.content_category = customData.content_category;
        
        // إضافة search parameters
        if (customData.search_term) eventParams.search_term = customData.search_term;
        
        // إضافة form parameters
        if (customData.form_type) eventParams.form_type = customData.form_type;
        if (customData.form_id) eventParams.form_id = customData.form_id;
        if (customData.item_list_id) eventParams.item_list_id = customData.item_list_id;
        if (customData.item_list_name) eventParams.item_list_name = customData.item_list_name;
      }

      // إرسال page_view parameters
      if (ga4EventName === 'page_view') {
        if (customData?.page_title) eventParams.page_title = customData.page_title;
        eventParams.page_location =
          customData?.page_location ??
          (typeof window !== 'undefined' ? window.location.href : undefined);
        eventParams.page_path =
          customData?.page_path ??
          (typeof window !== 'undefined' ? window.location.pathname : undefined);
      }

      // إرسال الحدث
      gtag('event', ga4EventName, eventParams);
      
      return true;
    } catch (error) {
      return false;
    }
  }
}
